import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { environment } from '../../environments/environment';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RegisterApiService, PowerStation } from '../core/services/register-api.service';
import {
  GoogleMapsLocationService,
  InteractiveLocationMapHandle,
  PreviewLocationMapHandle,
} from '../core/services/google-maps-location.service';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  customClass: {
    popup: 'premium-toast-popup',
    title: 'premium-toast-title',
  },
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer);
    toast.addEventListener('mouseleave', Swal.resumeTimer);
  },
});

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RegisterComponent implements OnInit, OnDestroy, AfterViewInit {
  registerForm: FormGroup;
  activeStep = 0;
  totalSteps = 6;
  showFormPreview = false;
  Object = Object;

  // Power station dropdown data
  powerStations: PowerStation[] = [];
  stationsLoading = false;
  stationsError = false;

  // CTO Flow data
  ctoFlow: any[] = [];
  ctoFlowLoading = false;
  ctoFlowError = false;

  showProductDropdown = false;
  selectedProductIds: number[] = [];
  // --- Toggle dropdown open/close ---
  toggleProductDropdown() {
    this.showProductDropdown = !this.showProductDropdown;
    if (this.showProductDropdown) {
      this.showCategoryDropdown = false;
      this.showUnloadDropdown = false;
    }
  }

  // Dropdown data arrays and states
  ashCategoriesList: any[] = [];
  categoriesLoading = false;
  categoriesError = false;

  ashUnloadList: any[] = [];
  unloadLoading = false;
  unloadError = false;

  flyAshProducts: any[] = [];
  productsLoading = false;
  productsError = false;

  states: any[] = [];
  statesLoading = false;
  statesError = false;

  districts: any[] = [];
  districtsLoading = false;
  districtsError = false;

  taluks: any[] = [];
  taluksLoading = false;
  taluksError = false;

  villages: any[] = [];
  villagesLoading = false;
  villagesError = false;

  tariff: any[] = [];
  tariffLoading = false;
  tariffError = false;

  // Company Address Dropdown data and states
  companyDistricts: any[] = [];
  companyDistrictsLoading = false;
  companyDistrictsError = false;

  mobileCheckStatus: 'checking' | 'exists' | 'valid' | null = null;
  mobileCheckErrorMsg: string = '';

  mobileOtpForm: FormGroup;
  mobileOtpSent = false;
  mobileOtpVerified = false;
  mobileOtpGenerating = false;
  mobileOtpVerifying = false;
  mobileOtpResendLoading = false;
  mobileOtpError = '';
  mobileOtpSuccess = '';
  mobileOtpResendCountdown = 0;
  otpVerifiedMobile = '';
  readonly mobileOtpFields = ['otp1', 'otp2', 'otp3', 'otp4'] as const;
  private mobileOtpResendTimerId: ReturnType<typeof setInterval> | null = null;
  private readonly MOBILE_OTP_RESEND_SECONDS = 60;

  emailOtpForm: FormGroup;
  emailOtpSent = false;
  emailOtpVerified = false;
  emailOtpGenerating = false;
  emailOtpVerifying = false;
  emailOtpResendLoading = false;
  emailOtpError = '';
  emailOtpSuccess = '';
  emailOtpResendCountdown = 0;
  otpVerifiedEmail = '';
  readonly emailOtpFields = ['emailOtp1', 'emailOtp2', 'emailOtp3', 'emailOtp4'] as const;
  private emailOtpResendTimerId: ReturnType<typeof setInterval> | null = null;
  private readonly EMAIL_OTP_RESEND_SECONDS = 60;

  companyTaluks: any[] = [];
  companyTaluksLoading = false;
  companyTaluksError = false;

  companyVillages: any[] = [];
  companyVillagesLoading = false;
  companyVillagesError = false;

  @ViewChild('plantMapContainer') plantMapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('companyMapContainer') companyMapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('plantLocationSearch') plantLocationSearch?: ElementRef<HTMLInputElement>;
  @ViewChild('companyLocationSearch') companyLocationSearch?: ElementRef<HTMLInputElement>;
  @ViewChild('previewPlantMapContainer') previewPlantMapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('previewCompanyMapContainer') previewCompanyMapContainer?: ElementRef<HTMLDivElement>;

  locationMapError = '';
  previewMapError = '';
  private readonly defaultMapCenter = { lat: 11.1271, lng: 78.6569 };
  private readonly defaultMapZoom = 7;
  private readonly selectedMapZoom = 16;
  private readonly addressStepIndex = 2;
  private readonly previewStepIndex = 5;
  private plantLocationMap: InteractiveLocationMapHandle | null = null;
  private companyLocationMap: InteractiveLocationMapHandle | null = null;
  private previewPlantMap: PreviewLocationMapHandle | null = null;
  private previewCompanyMap: PreviewLocationMapHandle | null = null;
  private locationMapInitTimer: ReturnType<typeof setTimeout> | null = null;
  private previewMapInitTimer: ReturnType<typeof setTimeout> | null = null;

  documentTypes: any[] = [];
  docsLoading = false;
  docsError = false;

  // Track file upload states
  plantMachineriesTags: string[] = [];

  addMachineryTag(event: any, inputElement: HTMLInputElement) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const value = inputElement.value.trim().replace(/,$/, '');
      if (value && !this.plantMachineriesTags.includes(value)) {
        this.plantMachineriesTags.push(value);
        this.registerForm.patchValue({ plantMachinery: this.plantMachineriesTags.join(', ') });
      }
      inputElement.value = '';
    }
  }

  removeMachineryTag(index: number) {
    this.plantMachineriesTags.splice(index, 1);
    this.registerForm.patchValue({ plantMachinery: this.plantMachineriesTags.join(', ') });
  }

  uploadedFiles: { [key: string]: File | null } = {
    gstCertificate: null,
    panCard: null,
    msmeCertificate: null,
    previousEbBill: null,
    gstr3b: null,
    ctoDocument: null,
    landOwnership: null,
    propertyTax: null,
    gpsMapCameraPhoto: null,
  };

  uploadedFileIds: { [key: string]: string | null } = {
    gstCertificate: null,
    panCard: null,
    msmeCertificate: null,
    previousEbBill: null,
    gstr3b: null,
    ctoDocument: null,
    landOwnership: null,
    propertyTax: null,
    gpsMapCameraPhoto: null,
  };

  docLabels: { [key: string]: string } = {
    gstCertificate: 'GST Certificate',
    panCard: 'PAN Card',
    msmeCertificate: 'MSmE / Udyam Certificate',
    previousEbBill: 'Previous 2 Bi-Month EB Bill',
    gstr3b: 'GST 3B Return(Previous 3 Months)',
    ctoDocument: 'TNPCB CTO Certtificate',
    landOwnership: 'Land Ownership / Lease Doc',
    propertyTax: 'Last Property Tax Paid Details',
    gpsMapCameraPhoto: 'GPS Map Camera Photo',
  };

  // Map component file keys to hardcoded API document type IDs
  documentTypeIds: { [key: string]: number } = {
    gstCertificate: 1,
    panCard: 2,
    msmeCertificate: 3,
    previousEbBill: 4,
    gstr3b: 5,
    ctoDocument: 24,
    landOwnership: 7,
    propertyTax: 8,
    gpsMapCameraPhoto: 11,
    declaretion: 9,
  };

  activeUploadDocKey: string | null = null;
  selectedFileForUpload: File | null = null;
  isUploading = false;
  uploadProgress = 0;

  // Custom Multi-Select Dropdown States
  showCategoryDropdown = false;
  showUnloadDropdown = false;
  showStepper = true; // controls sidebar stepper visibility
  stepperReady = false; // false on initial load — prevents transition flash

  toggleStepper() {
    this.showStepper = !this.showStepper;
  }

  toggleCategoryDropdown() {
    this.showCategoryDropdown = !this.showCategoryDropdown;
    if (this.showCategoryDropdown) {
      this.showUnloadDropdown = false;
      this.showProductDropdown = false;
    }
  }

  toggleUnloadDropdown() {
    this.showUnloadDropdown = !this.showUnloadDropdown;
    if (this.showUnloadDropdown) {
      this.showCategoryDropdown = false;
      this.showProductDropdown = false;
    }
  }

  getSelectedCategories(): any[] {
    const formArray = this.registerForm?.get('ashCategoryDetails') as FormArray;
    if (!formArray) return [];

    const capacity = Number(this.registerForm?.get('capacity')?.value) || 0;

    return formArray.value
      .map((val: any) => {
        const catObj = this.ashCategoriesList.find((c) => String(c.id) === String(val.categoryId));
        if (!catObj) return null;

        let calcMin = Number(val.minLoad || 0) * capacity;
        let calcMax = Number(val.maxLoad || 0) * capacity;
        if (calcMin > 0 && calcMin < 100) calcMin = 100;
        if (calcMax > 0 && calcMax < 100) calcMax = 100;

        return {
          ...catObj,
          minLoad: val.minLoad,
          maxLoad: val.maxLoad,
          minQuantity: calcMin,
          maxQuantity: calcMax
        };
      })
      .filter((c: any) => c != null);
  }

  removeCategory(id: any) {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    if (formArray) {
      const idx = formArray.controls.findIndex((ctrl) => ctrl.value.categoryId === id);
      if (idx > -1) {
        formArray.removeAt(idx);
        this.cdr.detectChanges();
      }
    }
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private el: ElementRef,
    private registerApiService: RegisterApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private googleMapsLocationService: GoogleMapsLocationService,
  ) {
    this.registerForm = this.fb.group({
      station: ['', Validators.required],
      ashCategoryDetails: this.fb.array([], Validators.required),
      ashUnloadingFacilities: this.fb.array([], Validators.required),
      firmName: ['', Validators.required],
      mobileNumber: ['', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$')]],
      emailAddress: ['', [Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/)]],
      gstNumber: [
        '',
        [
          Validators.required,
          Validators.pattern('^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$'),
        ],
      ],
      panNumber: ['', [Validators.required, Validators.pattern('^[A-Z]{5}[0-9]{4}[A-Z]{1}$')]],
      msmeCertificateType: ['', Validators.required],
      msmeRegistrationNumber: ['', [Validators.pattern('^UDYAM-[A-Z]{2}-\\d{2}-\\d{7}$')]],
      tnpgclScNumber: ['', [Validators.required, Validators.pattern(/^\d{12}$/)]],
      tariff: ['', Validators.required],
      consentToOperate: [''],
      state: ['Tamil Nadu', Validators.required],
      district: ['', Validators.required],
      taluk: ['', Validators.required],
      village: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]],
      doorNo: ['', Validators.required],
      surveyFieldNo: ['', Validators.required],
      streetLocation: ['', Validators.required],
      latitude: [''],
      longitude: [''],
      plantPhotoWithGps: [''],
      sameAsPlantAddress: [false],
      companyState: ['Tamil Nadu'],
      companyDistrict: [''],
      companyTaluk: [''],
      companyVillage: [''],
      companyPincode: [''],
      companyDoorNo: [''],
      companySurveyFieldNo: [''],
      companyStreetLocation: [''],
      companyLatitude: [''],
      companyLongitude: [''],
      productType: ['', Validators.required],
      capacity: ['', Validators.required],
      productDescription: [''],
      // `uploadingFacility` legacy control removed from template; keep optional if present
      // (was previously required) - keep as optional empty control to avoid template mismatch
      uploadingFacility: [''],
      plantMachinery: ['', Validators.required],
      ctoExemptedDetails: [''],

      declarationCheck: [false, Validators.requiredTrue],
      declarationCheck2: [false, Validators.requiredTrue],
    });

    this.mobileOtpForm = this.createMobileOtpForm();
    this.emailOtpForm = this.createEmailOtpForm();
  }

  private createMobileOtpForm(): FormGroup {
    const controls: Record<string, unknown[]> = {};
    for (const field of this.mobileOtpFields) {
      controls[field] = ['', [Validators.required, Validators.pattern(/^\d$/)]];
    }
    return this.fb.group(controls);
  }

  private createEmailOtpForm(): FormGroup {
    const controls: Record<string, unknown[]> = {};
    for (const field of this.emailOtpFields) {
      controls[field] = ['', [Validators.required, Validators.pattern(/^\d$/)]];
    }
    return this.fb.group(controls);
  }

  private documentClickListener: any;

  ngOnInit() {
    document.body.classList.add('register-page');

    // Close multi-select dropdowns when clicking outside
    this.documentClickListener = () => {
      let changed = false;
      if (this.showCategoryDropdown) {
        this.showCategoryDropdown = false;
        changed = true;
      }
      if (this.showUnloadDropdown) {
        this.showUnloadDropdown = false;
        changed = true;
      }
      if (this.showProductDropdown) {
        this.showProductDropdown = false;
        changed = true;
      }
      if (changed) {
        this.cdr.detectChanges();
      }
    };
    window.addEventListener('click', this.documentClickListener);

    // Load initial dropdowns
    this.loadPowerStations();
    this.loadFlyAshProducts();
    this.loadStates();
    this.loadDocumentTypes();
    this.loadTariff();
    this.loadAshAshUnloading();

    // Subscribe to Power Station changes
    this.registerForm.get('station')?.valueChanges.subscribe((stationId) => {
      this.ashCategoriesList = [];
      const ashCategoryArray = this.registerForm.get('ashCategoryDetails') as FormArray;
      if (ashCategoryArray) {
        ashCategoryArray.clear();
      }
      this.checkAndLoadAshCategories();
    });

    // Subscribe to State changes
    this.registerForm.get('state')?.valueChanges.subscribe((stateId) => {
      this.districts = [];
      this.taluks = [];
      this.villages = [];
      this.registerForm.get('district')?.setValue('');
      this.registerForm.get('taluk')?.setValue('');
      this.registerForm.get('village')?.setValue('');
      if (stateId) {
        this.loadDistricts(stateId);
      }
    });

    // Subscribe to District changes
    this.registerForm.get('district')?.valueChanges.subscribe((districtId) => {
      this.taluks = [];
      this.villages = [];
      this.registerForm.get('taluk')?.setValue('');
      this.registerForm.get('village')?.setValue('');
      if (districtId) {
        this.loadTaluks(districtId);
      }
    });

    // Subscribe to Taluk changes
    this.registerForm.get('taluk')?.valueChanges.subscribe((talukId) => {
      this.villages = [];
      this.registerForm.get('village')?.setValue('');
      if (talukId) {
        this.loadVillages(talukId);
      }
    });

    // Subscribe to sameAsPlantAddress checkbox
    this.registerForm.get('sameAsPlantAddress')?.valueChanges.subscribe((isSame) => {
      const companyStateControl = this.registerForm.get('companyState');
      const companyDistrictControl = this.registerForm.get('companyDistrict');
      const companyTalukControl = this.registerForm.get('companyTaluk');
      const companyVillageControl = this.registerForm.get('companyVillage');
      const companyPincodeControl = this.registerForm.get('companyPincode');
      const companyDoorNoControl = this.registerForm.get('companyDoorNo');
      const companySurveyFieldNoControl = this.registerForm.get('companySurveyFieldNo');
      const companyStreetLocationControl = this.registerForm.get('companyStreetLocation');
      const companyLatitudeControl = this.registerForm.get('companyLatitude');
      const companyLongitudeControl = this.registerForm.get('companyLongitude');

      if (isSame) {
        // Copy plant address to company address (optional but good for consistency)
        companyStateControl?.setValue(this.registerForm.get('state')?.value);
        companyDistrictControl?.setValue(this.registerForm.get('district')?.value);
        companyTalukControl?.setValue(this.registerForm.get('taluk')?.value);
        companyVillageControl?.setValue(this.registerForm.get('village')?.value);
        companyPincodeControl?.setValue(this.registerForm.get('pincode')?.value);
        companyDoorNoControl?.setValue(this.registerForm.get('doorNo')?.value);
        companySurveyFieldNoControl?.setValue(this.registerForm.get('surveyFieldNo')?.value);
        companyStreetLocationControl?.setValue(this.registerForm.get('streetLocation')?.value);
        companyLatitudeControl?.setValue(this.registerForm.get('latitude')?.value);
        companyLongitudeControl?.setValue(this.registerForm.get('longitude')?.value);

        // Clear all validators for company address since they are hidden and handled in onSubmit
        companyDistrictControl?.clearValidators();
        companyTalukControl?.clearValidators();
        companyVillageControl?.clearValidators();
        companyPincodeControl?.clearValidators();
        companyDoorNoControl?.clearValidators();
        companySurveyFieldNoControl?.clearValidators();
        companyStreetLocationControl?.clearValidators();
        companyLatitudeControl?.clearValidators();
        companyLongitudeControl?.clearValidators();
        companyDistrictControl?.updateValueAndValidity();
        companyTalukControl?.updateValueAndValidity();
        companyVillageControl?.updateValueAndValidity();
        companyPincodeControl?.updateValueAndValidity();
        companyDoorNoControl?.updateValueAndValidity();
        companySurveyFieldNoControl?.updateValueAndValidity();
        companyStreetLocationControl?.updateValueAndValidity();
        companyLatitudeControl?.updateValueAndValidity();
        companyLongitudeControl?.updateValueAndValidity();
        this.companyLocationMap?.destroy();
        this.companyLocationMap = null;
      } else {
        this.companyLocationMap?.destroy();
        this.companyLocationMap = null;
        // Clear all company address fields when unchecked
        const tn = this.states.find(
          (s: any) => s.name?.toLowerCase().replace(/\s/g, '') === 'tamilnadu',
        );
        companyStateControl?.setValue(tn ? tn.id : '');
        companyDistrictControl?.setValue('');
        companyTalukControl?.setValue('');
        companyVillageControl?.setValue('');
        companyPincodeControl?.setValue('');
        companyDoorNoControl?.setValue('');
        companySurveyFieldNoControl?.setValue('');
        companyStreetLocationControl?.setValue('');
        companyLatitudeControl?.setValue('');
        companyLongitudeControl?.setValue('');

        // Reset company dropdown data
        this.companyDistricts = [];
        this.companyTaluks = [];
        this.companyVillages = [];

        // Add validators for company address
        companyDistrictControl?.setValidators(Validators.required);
        companyTalukControl?.setValidators(Validators.required);
        companyVillageControl?.setValidators(Validators.required);
        companyPincodeControl?.setValidators([
          Validators.required,
          Validators.pattern('^[0-9]{6}$'),
        ]);
        companyDoorNoControl?.setValidators(Validators.required);
        companySurveyFieldNoControl?.setValidators(Validators.required);
        companyStreetLocationControl?.setValidators(Validators.required);
        companyLatitudeControl?.clearValidators();
        companyLongitudeControl?.clearValidators();
        companyDistrictControl?.updateValueAndValidity();
        companyTalukControl?.updateValueAndValidity();
        companyVillageControl?.updateValueAndValidity();
        companyPincodeControl?.updateValueAndValidity();
        companyDoorNoControl?.updateValueAndValidity();
        companySurveyFieldNoControl?.updateValueAndValidity();
        companyStreetLocationControl?.updateValueAndValidity();
        companyLatitudeControl?.updateValueAndValidity();
        companyLongitudeControl?.updateValueAndValidity();
        this.refreshMapSections();
      }
    });

    // Subscribe to Company State changes
    this.registerForm.get('companyState')?.valueChanges.subscribe((stateId) => {
      this.companyDistricts = [];
      this.companyTaluks = [];
      this.companyVillages = [];
      this.registerForm.get('companyDistrict')?.setValue('');
      this.registerForm.get('companyTaluk')?.setValue('');
      this.registerForm.get('companyVillage')?.setValue('');
      if (stateId) {
        this.loadCompanyDistricts(stateId);
      }
    });

    // Subscribe to Company District changes
    this.registerForm.get('companyDistrict')?.valueChanges.subscribe((districtId) => {
      this.companyTaluks = [];
      this.companyVillages = [];
      this.registerForm.get('companyTaluk')?.setValue('');
      this.registerForm.get('companyVillage')?.setValue('');
      if (districtId) {
        this.loadCompanyTaluks(districtId);
      }
    });

    // Subscribe to Company Taluk changes
    this.registerForm.get('companyTaluk')?.valueChanges.subscribe((talukId) => {
      this.companyVillages = [];
      this.registerForm.get('companyVillage')?.setValue('');
      if (talukId) {
        this.loadCompanyVillages(talukId);
      }
    });

    this.registerForm.get('panNumber')?.valueChanges.subscribe((value) => {
      if (value) {
        this.registerForm.get('panNumber')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    this.registerForm.get('gstNumber')?.valueChanges.subscribe((value) => {
      if (value) {
        this.registerForm.get('gstNumber')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    this.registerForm.get('msmeCertificateType')?.valueChanges.subscribe((value) => {
      // Clear the consentToOperate value when MSME type changes
      // to ensure they make a fresh selection/input for the new field type
      this.registerForm.get('consentToOperate')?.setValue('');
      this.registerForm.get('consentToOperate')?.clearValidators();
      this.registerForm.get('consentToOperate')?.updateValueAndValidity();
      this.checkAndLoadAshCategories();
      // Reset cached CTO flow so it re-fetches when user reaches step 4
      this.ctoFlow = [];
    });

    this.registerForm.get('productType')?.valueChanges.subscribe(() => {
      this.checkAndLoadAshCategories();
      // Reset cached CTO flow so it re-fetches with the updated product selection on step 4
      this.ctoFlow = [];
    });

    this.registerForm.get('mobileNumber')?.valueChanges.subscribe((value) => {
      const mobile = String(value || '');

      if (mobile !== this.otpVerifiedMobile) {
        this.resetMobileOtpState();
      }

      if (mobile.length === 10 && /^[6-9][0-9]{9}$/.test(mobile)) {
        this.mobileCheckStatus = 'checking';
        this.cdr.detectChanges();
        this.registerApiService.checkMobileExists(value).subscribe({
          next: (res) => {
            // The API returns status: false if it's already registered
            if (res && res.status === false) {
              this.mobileCheckStatus = 'exists';
              this.mobileCheckErrorMsg = res.message || 'This mobile number is already registered!';
              this.registerForm.get('mobileNumber')?.setErrors({ alreadyRegistered: true });
            } else {
              this.mobileCheckStatus = 'valid';
              this.mobileCheckErrorMsg = '';
              const currentErrors = this.registerForm.get('mobileNumber')?.errors;
              if (currentErrors) {
                delete currentErrors['alreadyRegistered'];
                if (Object.keys(currentErrors).length === 0) {
                  this.registerForm.get('mobileNumber')?.setErrors(null);
                } else {
                  this.registerForm.get('mobileNumber')?.setErrors(currentErrors);
                }
              }
            }
            this.cdr.detectChanges();
          },
          error: (err) => {
            const errMsg = err?.error?.message || 'Error validating mobile number.';
            if (
              (errMsg || '').toLowerCase().includes('already') ||
              (errMsg || '').toLowerCase().includes('pending') ||
              err?.error?.status === false
            ) {
              this.mobileCheckStatus = 'exists';
              this.mobileCheckErrorMsg = errMsg;
              this.registerForm.get('mobileNumber')?.setErrors({ alreadyRegistered: true });
            } else {
              this.mobileCheckStatus = 'valid';
              this.mobileCheckErrorMsg = '';
            }
            this.cdr.detectChanges();
          },
        });
      } else {
        this.mobileCheckStatus = null;
        this.cdr.detectChanges();
      }
    });

    this.registerForm.get('emailAddress')?.valueChanges.subscribe((value) => {
      const email = String(value || '')
        .trim()
        .toLowerCase();

      if (this.emailOtpVerified && email === this.otpVerifiedEmail) {
        return;
      }

      if (this.emailOtpVerifying) {
        return;
      }

      if (email !== this.otpVerifiedEmail) {
        this.resetEmailOtpState();
      }
    });
  }

  // --- API Loaders ---

  private extractData(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.items)) return res.items;
    return [];
  }

  loadPowerStations() {
    this.stationsLoading = true;
    this.stationsError = false;
    this.registerApiService
      .getPowerStations()
      .pipe(
        finalize(() => {
          this.stationsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.powerStations =
            this.extractData(res) || (Array.isArray(res?.stations) ? res.stations : []);
        },
        error: (err) => {
          console.error('Error loading stations:', err);
          this.stationsError = true;
        },
      });
  }

  loadAshCategories(stationId: string | number, msme?: string, products?: any[]) {
    this.categoriesLoading = true;
    this.categoriesError = false;
    this.registerApiService
      .getAshCategories(stationId, msme, products)
      .pipe(
        finalize(() => {
          this.categoriesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.ashCategoriesList = this.extractData(res);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading ash categories:', err);
          this.categoriesError = true;
          this.cdr.detectChanges();
        },
      });
  }

  checkAndLoadAshCategories() {
    const stationId = this.registerForm.get('station')?.value;
    const msme = this.registerForm.get('msmeCertificateType')?.value;
    const products = this.selectedProductIds;

    if (stationId && msme && products && products.length > 0) {
      this.loadAshCategories(stationId, msme, products);
    } else {
      this.ashCategoriesList = [];
      const ashCategoryArray = this.registerForm.get('ashCategoryDetails') as FormArray;
      if (ashCategoryArray) {
        ashCategoryArray.clear();
      }
    }
  }

  loadAshAshUnloading() {
    this.unloadLoading = true;
    this.unloadError = false;
    this.registerApiService
      .getAshUnloadingFacilities()
      .pipe(
        finalize(() => {
          this.unloadLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.ashUnloadList = this.extractData(res);
          console.log('Loaded ash unloading facilities:', this.ashUnloadList);
        },
        error: (err) => {
          console.error('Error loading ash categories:', err);
          this.unloadError = true;
        },
      });
  }

  loadTariff() {
    this.tariffLoading = true;
    this.tariffError = false;
    this.registerApiService
      .getTariff()
      .pipe(
        finalize(() => {
          this.tariffLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.tariff = this.extractData(res);
          console.log('Loaded tariff data:', this.tariff);
        },
        error: (err) => {
          console.error('Error loading ash tariff:', err);
          this.tariffError = true;
        },
      });
  }

  // --- Check if a product is selected ---
  isFlyAshProductChecked(id: number | string): boolean {
    return this.selectedProductIds.some((sid) => String(sid) === String(id));
  }

  loadCtoFlow(): void {
    if (this.ctoFlow.length > 0) return; // already loaded

    // Get selected product IDs to send as query param (API requires product)
    const msme = this.registerForm.get('msmeCertificateType')?.value;
    const products = this.selectedProductIds;
    if (!products || products.length === 0) {
      // No product selected yet — will retry when step is entered after products are chosen
      this.ctoFlowError = true;
      console.warn('loadCtoFlow: no products selected, skipping API call');
      return;
    }

    this.ctoFlowLoading = true;
    this.ctoFlowError = false;
    this.registerApiService
      .getCtoFlow(msme, products)
      .subscribe({
        next: (res) => {
          this.ctoFlowLoading = false;
          this.ctoFlow = this.extractData(res) || [];
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.ctoFlowLoading = false;
          this.ctoFlowError = true;
          console.error('Error loading CTO flow:', err);
          this.cdr.detectChanges();
        },
      });
  }


  isOthersProductSelected(): boolean {
    return this.getSelectedProducts().some(
      (p) => (p.name || '').toLowerCase() === 'others' || (p.name || '').toLowerCase() === 'other',
    );
  }

  updateProductDescriptionValidation() {
    const descControl = this.registerForm.get('productDescription');
    if (this.isOthersProductSelected()) {
      descControl?.setValidators([Validators.required]);
    } else {
      descControl?.clearValidators();
      descControl?.setValue('');
    }
    descControl?.updateValueAndValidity();
  }

  // --- Toggle a product selection ---
  toggleFlyAshProduct(id: number) {
    const index = this.selectedProductIds.indexOf(id);
    if (index === -1) {
      this.selectedProductIds.push(id);
    } else {
      this.selectedProductIds.splice(index, 1);
    }
    // Sync to form control
    this.registerForm
      .get('productType')
      ?.setValue(this.selectedProductIds.length ? this.selectedProductIds : null);
    this.registerForm.get('productType')?.markAsTouched();
    this.updateProductDescriptionValidation();

    // Reload CTO Flow when products change
    this.ctoFlow = [];
    this.loadCtoFlow();
  }

  // --- Get full objects of selected products (for badge display) ---
  getSelectedProducts(): { id: number; name: string }[] {
    return this.flyAshProducts.filter((p) =>
      this.selectedProductIds.some((sid) => String(sid) === String(p.id)),
    );
  }

  // --- Remove a product badge ---
  removeProduct(id: number | string) {
    this.selectedProductIds = this.selectedProductIds.filter((pid) => String(pid) !== String(id));
    this.registerForm
      .get('productType')
      ?.setValue(this.selectedProductIds.length ? this.selectedProductIds : null);
    this.registerForm.get('productType')?.markAsTouched();
    this.updateProductDescriptionValidation();

    // Reload CTO Flow when products change
    this.ctoFlow = [];
    this.loadCtoFlow();
  }

  getSelectedUnload(): any[] {
    const formArray = this.registerForm?.get('ashUnloadingFacilities') as FormArray;
    if (!formArray) return [];
    const selectedIds = formArray.value || [];
    return this.ashUnloadList.filter(
      (item) =>
        selectedIds.includes(item.id) ||
        selectedIds.includes(String(item.id)) ||
        selectedIds.includes(Number(item.id)),
    );
  }

  removeUnload(id: any) {
    const formArray = this.registerForm.get('ashUnloadingFacilities') as FormArray;
    if (formArray) {
      const idx = formArray.controls.findIndex((ctrl) => ctrl.value === id);
      if (idx > -1) {
        formArray.removeAt(idx);
      } else {
        const numIdx = formArray.controls.findIndex((ctrl) => ctrl.value === Number(id));
        if (numIdx > -1) {
          formArray.removeAt(numIdx);
        }
      }
      formArray.markAsTouched();
      this.cdr.detectChanges();
    }
  }

  toggleUnloadFacility(id: any) {
    const formArray = this.registerForm.get('ashUnloadingFacilities') as FormArray;
    const idx = formArray.controls.findIndex(
      (ctrl) => ctrl.value === id || ctrl.value === Number(id) || ctrl.value === String(id),
    );
    if (idx > -1) {
      formArray.removeAt(idx);
    } else {
      formArray.push(this.fb.control(id));
    }
    formArray.markAsTouched();
  }

  isUnloadFacilityChecked(id: any): boolean {
    const formArray = this.registerForm.get('ashUnloadingFacilities') as FormArray;
    return formArray.value.some(
      (val: any) => val === id || val === Number(id) || val === String(id),
    );
  }

  loadFlyAshProducts() {
    this.productsLoading = true;
    this.productsError = false;
    this.registerApiService
      .getFlyAshProducts()
      .pipe(
        finalize(() => {
          this.productsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.flyAshProducts = this.extractData(res);
          console.log('flyAshProducts', this.flyAshProducts);
        },
        error: (err) => {
          console.error('Error loading products:', err);
          this.productsError = true;
        },
      });
  }

  loadStates() {
    this.statesLoading = true;
    this.statesError = false;
    this.registerApiService
      .getStates()
      .pipe(
        finalize(() => {
          this.statesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.states = this.extractData(res);
          const tn = this.states.find(
            (s: any) => s.name?.toLowerCase().replace(/\s/g, '') === 'tamilnadu',
          );
          if (tn) {
            this.registerForm.patchValue({ state: tn.id, companyState: tn.id });
          }
        },
        error: (err) => {
          console.error('Error loading states:', err);
          this.statesError = true;
        },
      });
  }

  loadDistricts(stateId: string | number) {
    this.districtsLoading = true;
    this.districtsError = false;
    this.registerApiService
      .getDistricts(stateId)
      .pipe(
        finalize(() => {
          this.districtsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.districts = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading districts:', err);
          this.districtsError = true;
        },
      });
  }

  loadTaluks(districtId: string | number) {
    this.taluksLoading = true;
    this.taluksError = false;
    this.registerApiService
      .getTaluks(districtId)
      .pipe(
        finalize(() => {
          this.taluksLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.taluks = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading taluks:', err);
          this.taluksError = true;
        },
      });
  }

  loadVillages(talukId: string | number) {
    this.villagesLoading = true;
    this.villagesError = false;
    this.registerApiService
      .getVillages(talukId)
      .pipe(
        finalize(() => {
          this.villagesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.villages = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading villages:', err);
          this.villagesError = true;
        },
      });
  }

  // Company Address Loaders
  loadCompanyDistricts(stateId: string | number) {
    this.companyDistrictsLoading = true;
    this.companyDistrictsError = false;
    this.registerApiService
      .getDistricts(stateId)
      .pipe(
        finalize(() => {
          this.companyDistrictsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.companyDistricts = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading company districts:', err);
          this.companyDistrictsError = true;
        },
      });
  }

  loadCompanyTaluks(districtId: string | number) {
    this.companyTaluksLoading = true;
    this.companyTaluksError = false;
    this.registerApiService
      .getTaluks(districtId)
      .pipe(
        finalize(() => {
          this.companyTaluksLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.companyTaluks = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading company taluks:', err);
          this.companyTaluksError = true;
        },
      });
  }

  loadCompanyVillages(talukId: string | number) {
    this.companyVillagesLoading = true;
    this.companyVillagesError = false;
    this.registerApiService
      .getVillages(talukId)
      .pipe(
        finalize(() => {
          this.companyVillagesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.companyVillages = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading company villages:', err);
          this.companyVillagesError = true;
        },
      });
  }

  loadDocumentTypes() {
    this.docsLoading = true;
    this.docsError = false;
    this.registerApiService
      .getDocumentTypes()
      .pipe(
        finalize(() => {
          this.docsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.documentTypes = this.extractData(res);
        },
        error: (err) => {
          console.error('Error loading doc types:', err);
          this.docsError = true;
        },
      });
  }

  ngOnDestroy() {
    document.body.classList.remove('register-page');
    if (this.documentClickListener) {
      window.removeEventListener('click', this.documentClickListener);
    }
    this.clearMobileOtpResendTimer();
    this.clearEmailOtpResendTimer();
    if (this.locationMapInitTimer) {
      clearTimeout(this.locationMapInitTimer);
    }
    if (this.previewMapInitTimer) {
      clearTimeout(this.previewMapInitTimer);
    }
    this.destroyInteractiveLocationMaps();
    this.destroyPreviewLocationMaps();
  }

  get isMobileOtpComplete(): boolean {
    return this.mobileOtpFields.every((field) => this.mobileOtpForm.get(field)?.valid);
  }

  get canGenerateMobileOtp(): boolean {
    return (
      this.mobileCheckStatus === 'valid' &&
      !this.mobileOtpVerified &&
      !this.mobileOtpGenerating &&
      !this.mobileOtpSent
    );
  }

  onGenerateMobileOtp(): void {
    const mobileNo = String(this.registerForm.get('mobileNumber')?.value || '').trim();
    if (!mobileNo || this.mobileCheckStatus !== 'valid') {
      return;
    }

    this.mobileOtpError = '';
    this.mobileOtpSuccess = '';
    this.mobileOtpGenerating = true;

    this.registerApiService.generateMobileOtp(mobileNo).subscribe({
      next: (res) => {
        this.mobileOtpGenerating = false;
        if (this.isApiSuccess(res)) {
          this.mobileOtpSent = true;
          this.mobileOtpSuccess =
            this.parseApiMessage(res?.message) || 'OTP sent to your mobile number.';
          this.resetMobileOtpForm();
          this.registerForm.get('mobileNumber')?.disable({ emitEvent: false });
          this.startMobileOtpResendCountdown();
          this.cdr.detectChanges();
        } else {
          this.mobileOtpError =
            this.parseApiMessage(res?.message) || 'Unable to generate OTP. Please try again.';
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.mobileOtpGenerating = false;
        this.mobileOtpError =
          this.parseApiMessage(err?.error?.message) ||
          'Unable to generate OTP. Please try again later.';
        this.cdr.detectChanges();
      },
    });
  }

  onResendMobileOtp(): void {
    if (this.mobileOtpResendCountdown > 0 || this.mobileOtpResendLoading || !this.mobileOtpSent) {
      return;
    }

    const mobileNo = String(this.registerForm.get('mobileNumber')?.value || '').trim();
    if (!mobileNo) {
      return;
    }

    this.mobileOtpError = '';
    this.mobileOtpResendLoading = true;

    this.registerApiService.resendMobileOtp(mobileNo).subscribe({
      next: (res) => {
        this.mobileOtpResendLoading = false;
        if (this.isApiSuccess(res)) {
          this.mobileOtpSuccess =
            this.parseApiMessage(res?.message) || 'OTP resent to your mobile number.';
          this.resetMobileOtpForm();
          this.startMobileOtpResendCountdown();
        } else {
          this.mobileOtpError =
            this.parseApiMessage(res?.message) || 'Unable to resend OTP. Please try again.';
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.mobileOtpResendLoading = false;
        this.mobileOtpError =
          this.parseApiMessage(err?.error?.message) ||
          'Unable to resend OTP. Please try again later.';
        this.cdr.detectChanges();
      },
    });
  }

  onVerifyMobileOtp(): void {
    this.mobileOtpForm.markAllAsTouched();
    this.mobileOtpError = '';

    if (!this.isMobileOtpComplete) {
      this.mobileOtpError = 'OTP is required. Please enter the complete 4-digit code.';
      this.cdr.detectChanges();
      return;
    }

    const mobileNo = String(this.registerForm.get('mobileNumber')?.value || '').trim();
    const otp = this.getMobileOtpValue();
    this.mobileOtpVerifying = true;

    this.registerApiService.verifyMobileOtp(mobileNo, otp).subscribe({
      next: (res) => {
        this.mobileOtpVerifying = false;
        if (this.isApiSuccess(res)) {
          this.mobileOtpVerified = true;
          this.otpVerifiedMobile = mobileNo;
          this.mobileOtpSuccess =
            this.parseApiMessage(res?.message) || 'Mobile number verified successfully.';
          this.mobileOtpError = '';
          this.clearMobileOtpResendTimer();
        } else {
          this.mobileOtpError =
            this.parseApiMessage(res?.message) || 'Invalid OTP. Please try again.';
          this.resetMobileOtpForm();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.mobileOtpVerifying = false;
        this.mobileOtpError =
          this.parseApiMessage(err?.error?.message) || 'Invalid OTP. Please check and try again.';
        this.resetMobileOtpForm();
        this.cdr.detectChanges();
      },
    });
  }

  changeMobileNumber(): void {
    this.resetMobileOtpState();
    this.mobileCheckStatus = null;
    this.mobileCheckErrorMsg = '';
    this.cdr.detectChanges();
  }

  get isEmailOtpComplete(): boolean {
    return this.emailOtpFields.every((field) => this.emailOtpForm.get(field)?.valid);
  }

  get canGenerateEmailOtp(): boolean {
    const emailCtrl = this.registerForm.get('emailAddress');
    return (
      !!emailCtrl &&
      emailCtrl.valid &&
      !!String(emailCtrl.value || '').trim() &&
      !this.emailOtpVerified &&
      !this.emailOtpGenerating &&
      !this.emailOtpSent
    );
  }

  onGenerateEmailOtp(): void {
    const email = this.getEmailAddress();
    const emailCtrl = this.registerForm.get('emailAddress');
    if (!email || !emailCtrl?.valid) {
      return;
    }

    this.emailOtpError = '';
    this.emailOtpSuccess = '';
    this.emailOtpGenerating = true;
    this.refreshView();

    this.registerApiService
      .generateEmailOtp(email)
      .pipe(finalize(() => this.refreshView()))
      .subscribe({
        next: (res) => {
          this.emailOtpGenerating = false;
          if (this.isApiSuccess(res)) {
            this.emailOtpSent = true;
            this.emailOtpSuccess =
              this.parseApiMessage(res?.message) || 'OTP sent to your email address.';
            this.resetEmailOtpForm();
            this.registerForm.get('emailAddress')?.disable({ emitEvent: false });
            this.startEmailOtpResendCountdown();
          } else {
            this.emailOtpError =
              this.parseApiMessage(res?.message) || 'Unable to generate OTP. Please try again.';
          }
        },
        error: (err) => {
          this.emailOtpGenerating = false;
          this.emailOtpError =
            this.parseApiMessage(err?.error?.message) ||
            'Unable to generate OTP. Please try again later.';
        },
      });
  }

  onResendEmailOtp(): void {
    if (this.emailOtpResendCountdown > 0 || this.emailOtpResendLoading || !this.emailOtpSent) {
      return;
    }

    const email = this.getEmailAddress();
    if (!email) {
      return;
    }

    this.emailOtpError = '';
    this.emailOtpResendLoading = true;
    this.refreshView();

    this.registerApiService
      .resendEmailOtp(email)
      .pipe(finalize(() => this.refreshView()))
      .subscribe({
        next: (res) => {
          this.emailOtpResendLoading = false;
          if (this.isApiSuccess(res)) {
            this.emailOtpSuccess =
              this.parseApiMessage(res?.message) || 'OTP resent to your email address.';
            this.resetEmailOtpForm();
            this.startEmailOtpResendCountdown();
          } else {
            this.emailOtpError =
              this.parseApiMessage(res?.message) || 'Unable to resend OTP. Please try again.';
          }
        },
        error: (err) => {
          this.emailOtpResendLoading = false;
          this.emailOtpError =
            this.parseApiMessage(err?.error?.message) ||
            'Unable to resend OTP. Please try again later.';
        },
      });
  }

  onVerifyEmailOtp(): void {
    if (this.emailOtpVerifying) {
      return;
    }

    this.emailOtpForm.markAllAsTouched();
    this.emailOtpError = '';

    if (!this.isEmailOtpComplete) {
      this.emailOtpError = 'OTP is required. Please enter the complete 4-digit code.';
      this.refreshView();
      return;
    }

    const email = this.getEmailAddress();
    const otp = this.getEmailOtpValue();
    if (!email) {
      this.emailOtpError = 'Email address is missing. Please change and re-enter your email.';
      this.refreshView();
      return;
    }

    this.emailOtpVerifying = true;
    this.refreshView();

    this.registerApiService
      .verifyEmailOtp(email, otp)
      .pipe(
        finalize(() => {
          this.emailOtpVerifying = false;
          this.refreshView();
        }),
      )
      .subscribe({
        next: (res) => {
          this.clearEmailOtpResendTimer();
          if (this.isApiSuccess(res)) {
            this.emailOtpVerified = true;
            this.otpVerifiedEmail = email;
            this.emailOtpSuccess =
              this.parseApiMessage(res?.message) || 'Email verified successfully.';
            this.emailOtpError = '';
          } else {
            this.emailOtpError =
              this.parseApiMessage(res?.message) || 'Invalid OTP. Please try again.';
            this.resetEmailOtpForm();
          }
        },
        error: (err) => {
          this.emailOtpError =
            this.parseApiMessage(err?.error?.message) || 'Invalid OTP. Please check and try again.';
          this.resetEmailOtpForm();
        },
      });
  }

  changeEmailAddress(): void {
    this.resetEmailOtpState();
    this.refreshView();
  }

  onEmailOtpInput(event: Event, currentField: string, nextField: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    input.value = value;
    this.emailOtpForm.get(currentField)?.setValue(value);

    if (value && nextField) {
      const nextInput = document.getElementById(nextField) as HTMLInputElement | null;
      nextInput?.focus();
    }

    setTimeout(() => {
      if (this.isEmailOtpComplete) {
        this.onVerifyEmailOtp();
      }
    });
  }

  onEmailOtpKeydown(event: KeyboardEvent, currentField: string, prevField: string): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevField) {
      const prevInput = document.getElementById(prevField) as HTMLInputElement | null;
      prevInput?.focus();
    }
  }

  onEmailOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, this.emailOtpFields.length);

    this.emailOtpFields.forEach((field, index) => {
      this.emailOtpForm.get(field)?.setValue(digits[index] || '');
    });

    const focusIndex = Math.min(digits.length, this.emailOtpFields.length - 1);
    const focusInput = document.getElementById(
      this.emailOtpFields[focusIndex],
    ) as HTMLInputElement | null;
    focusInput?.focus();

    setTimeout(() => {
      if (this.isEmailOtpComplete) {
        this.onVerifyEmailOtp();
      }
    });
  }

  private getEmailAddress(): string {
    return String(this.registerForm.getRawValue().emailAddress || '')
      .trim()
      .toLowerCase();
  }

  private getEmailOtpValue(): string {
    return this.emailOtpFields.map((field) => this.emailOtpForm.get(field)?.value || '').join('');
  }

  private resetEmailOtpForm(): void {
    this.emailOtpForm.reset();
    for (const field of this.emailOtpFields) {
      this.emailOtpForm.get(field)?.setValue('');
    }
    setTimeout(() => {
      if (this.emailOtpFields.length > 0) {
        document.getElementById(this.emailOtpFields[0])?.focus();
      }
    });
  }

  private resetEmailOtpState(): void {
    this.emailOtpSent = false;
    this.emailOtpVerified = false;
    this.emailOtpGenerating = false;
    this.emailOtpVerifying = false;
    this.emailOtpResendLoading = false;
    this.emailOtpError = '';
    this.emailOtpSuccess = '';
    this.otpVerifiedEmail = '';
    this.clearEmailOtpResendTimer();
    this.resetEmailOtpForm();
    this.registerForm.get('emailAddress')?.enable({ emitEvent: false });
  }

  private startEmailOtpResendCountdown(): void {
    this.clearEmailOtpResendTimer();
    this.emailOtpResendCountdown = this.EMAIL_OTP_RESEND_SECONDS;
    this.emailOtpResendTimerId = setInterval(() => {
      if (this.emailOtpVerifying) {
        return;
      }
      this.emailOtpResendCountdown -= 1;
      if (this.emailOtpResendCountdown <= 0) {
        this.clearEmailOtpResendTimer();
      }
      this.refreshView();
    }, 1000);
  }

  private clearEmailOtpResendTimer(): void {
    if (this.emailOtpResendTimerId) {
      clearInterval(this.emailOtpResendTimerId);
      this.emailOtpResendTimerId = null;
    }
    this.emailOtpResendCountdown = 0;
  }

  private refreshView(): void {
    queueMicrotask(() => this.cdr.detectChanges());
  }

  onMobileOtpInput(event: Event, currentField: string, nextField: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    input.value = value;
    this.mobileOtpForm.get(currentField)?.setValue(value);

    if (value && nextField) {
      const nextInput = document.getElementById(nextField) as HTMLInputElement | null;
      nextInput?.focus();
    }

    setTimeout(() => {
      if (this.isMobileOtpComplete) {
        this.onVerifyMobileOtp();
      }
    });
  }

  onMobileOtpKeydown(event: KeyboardEvent, currentField: string, prevField: string): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevField) {
      const prevInput = document.getElementById(prevField) as HTMLInputElement | null;
      prevInput?.focus();
    }
  }

  onMobileOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, this.mobileOtpFields.length);

    this.mobileOtpFields.forEach((field, index) => {
      this.mobileOtpForm.get(field)?.setValue(digits[index] || '');
    });

    const focusIndex = Math.min(digits.length, this.mobileOtpFields.length - 1);
    const focusInput = document.getElementById(
      this.mobileOtpFields[focusIndex],
    ) as HTMLInputElement | null;
    focusInput?.focus();

    setTimeout(() => {
      if (this.isMobileOtpComplete) {
        this.onVerifyMobileOtp();
      }
    });
  }

  private getMobileOtpValue(): string {
    return this.mobileOtpFields.map((field) => this.mobileOtpForm.get(field)?.value || '').join('');
  }

  private resetMobileOtpForm(): void {
    this.mobileOtpForm.reset();
    for (const field of this.mobileOtpFields) {
      this.mobileOtpForm.get(field)?.setValue('');
    }
    setTimeout(() => {
      if (this.mobileOtpFields.length > 0) {
        document.getElementById(this.mobileOtpFields[0])?.focus();
      }
    });
  }

  private resetMobileOtpState(): void {
    this.mobileOtpSent = false;
    this.mobileOtpVerified = false;
    this.mobileOtpGenerating = false;
    this.mobileOtpVerifying = false;
    this.mobileOtpResendLoading = false;
    this.mobileOtpError = '';
    this.mobileOtpSuccess = '';
    this.otpVerifiedMobile = '';
    this.clearMobileOtpResendTimer();
    this.resetMobileOtpForm();
    this.registerForm.get('mobileNumber')?.enable({ emitEvent: false });
  }

  private startMobileOtpResendCountdown(): void {
    this.clearMobileOtpResendTimer();
    this.mobileOtpResendCountdown = this.MOBILE_OTP_RESEND_SECONDS;
    this.mobileOtpResendTimerId = setInterval(() => {
      this.mobileOtpResendCountdown -= 1;
      if (this.mobileOtpResendCountdown <= 0) {
        this.clearMobileOtpResendTimer();
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  private clearMobileOtpResendTimer(): void {
    if (this.mobileOtpResendTimerId) {
      clearInterval(this.mobileOtpResendTimerId);
      this.mobileOtpResendTimerId = null;
    }
    this.mobileOtpResendCountdown = 0;
  }

  private isApiSuccess(res: any): boolean {
    return res?.status === true || res?.status === 'true' || res?.status === 1;
  }

  private parseApiMessage(message: unknown): string {
    if (Array.isArray(message)) {
      return message
        .map((item) => (typeof item === 'string' ? item : String(item)))
        .filter(Boolean)
        .join(' ');
    }
    if (typeof message === 'string') {
      return message;
    }
    return '';
  }

  ngAfterViewInit() {
    // Enable sidebar transitions after first paint — prevents blank screen on load
    setTimeout(() => {
      this.stepperReady = true;
      this.cdr.detectChanges();
      this.refreshMapSections();
    }, 0);
  }

  getProgressPercent(): number {
    return Math.round(((this.activeStep + 1) / this.totalSteps) * 100);
  }

  isStepCompleted(step: number): boolean {
    return step < this.activeStep;
  }

  getStepControlNames(stepNum: number): string[] {
    switch (stepNum) {
      case 0:
        return ['station', 'mobileNumber'];
      case 1:
        return [
          'firmName',
          'emailAddress',
          'gstNumber',
          'panNumber',
          'msmeCertificateType',
          'msmeRegistrationNumber',
          'tnpgclScNumber',
          'tariff',
        ];
      case 2:
        const addressFields = [
          'doorNo',
          'surveyFieldNo',
          'streetLocation',
          'state',
          'district',
          'taluk',
          'village',
          'pincode',
        ];
        if (!this.registerForm.get('sameAsPlantAddress')?.value) {
          addressFields.push(
            'companyDoorNo',
            'companySurveyFieldNo',
            'companyStreetLocation',
            'companyState',
            'companyDistrict',
            'companyTaluk',
            'companyVillage',
            'companyPincode',
          );
        }
        return addressFields;
      case 3:
        return [
          'productType',
          'capacity',
          'plantMachinery',
          'ashUnloadingFacilities',
          'ashCategoryDetails',
          'consentToOperate',
        ];
      case 4:
        return ['declarationCheck', 'declarationCheck2'];
      default:
        return [];
    }
  }

  isStepValid(stepNum: number): boolean {
    const controls = this.getStepControlNames(stepNum);
    let valid = true;
    for (const name of controls) {
      const ctrl = this.registerForm.get(name);
      if (ctrl && ctrl.invalid) {
        valid = false;
      }
    }
    if (stepNum === 0 && !this.mobileOtpVerified) {
      valid = false;
    }
    if (stepNum === 1) {
      if (!this.emailOtpVerified) {
        valid = false;
      }
      if (!this.uploadedFiles['previousEbBill']) {
        valid = false;
      }
    }
    if (stepNum === 3) {
      // Require ctoDocument upload when consentToOperate is 'Non-Exempted'
      const ctoValue = this.registerForm.get('consentToOperate')?.value;
      if (ctoValue === 'Non-Exempted' && !this.uploadedFiles['ctoDocument']) {
        valid = false;
      }
    }
    if (stepNum === 2) {
      if (!this.uploadedFiles['gpsMapCameraPhoto']) {
        valid = false;
      }
    }
    // For step 4 (Documents), we also validate required file uploads
    if (stepNum === 4) {
      const requiredFiles = [
        'gstCertificate',
        'panCard',
        'previousEbBill',
        'gstr3b',
        'landOwnership',
        'propertyTax',
      ];
      const missingFiles = requiredFiles.filter((key) => !this.uploadedFiles[key]);
      if (missingFiles.length > 0) {
        valid = false;
      }
    }
    return valid;
  }

  markStepControlsAsTouched(stepNum: number) {
    const controls = this.getStepControlNames(stepNum);
    for (const name of controls) {
      const ctrl = this.registerForm.get(name);
      if (ctrl) {
        ctrl.markAsTouched();
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  setStep(n: number) {
    if (n < this.activeStep) {
      // Going backward is always allowed
      this.activeStep = n;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.refreshMapSections();
    } else if (n > this.activeStep) {
      // Going forward: check if all steps between current activeStep and n-1 are valid
      let canGoForward = true;
      for (let i = this.activeStep; i < n; i++) {
        if (!this.isStepValid(i)) {
          this.markStepControlsAsTouched(i);
          canGoForward = false;
          break;
        }
      }
      if (canGoForward) {
        this.activeStep = n;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.refreshMapSections();
        // Load CTO flow data when entering step 4 (Plant Operations, activeStep===3)
        if (this.activeStep === 3 && this.registerForm.get('msmeCertificateType')?.value) {
          this.loadCtoFlow();
        }
      } else {
        Toast.fire({
          icon: 'warning',
          title: 'Please fill all required fields in the current step correctly.',
        });
      }
    }
  }

  nextStep() {
    if (this.isStepValid(this.activeStep)) {
      // Auto-save silently on every step
      this.onSaveDraft(true);

      if (this.activeStep < 5) {
        this.activeStep++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.refreshMapSections();
        // Load CTO flow data when entering step 4 (Plant Operations, activeStep===3)
        if (this.activeStep === 3 && this.registerForm.get('msmeCertificateType')?.value) {
          this.loadCtoFlow();
        }
      }
    } else {
      this.markStepControlsAsTouched(this.activeStep);
      Toast.fire({
        icon: 'warning',
        title:
          this.activeStep === 0 && !this.mobileOtpVerified
            ? 'Please verify your mobile number with OTP before proceeding.'
            : this.activeStep === 1 && !this.emailOtpVerified
              ? 'Please verify your email address with OTP before proceeding.'
              : this.activeStep === 1 && !this.uploadedFiles['previousEbBill']
                ? 'Please upload TNEB Bill Status attachment before proceeding.'
                : this.activeStep === 2 && !this.uploadedFiles['gpsMapCameraPhoto']
                  ? 'Please upload Plant Photo with GPS Details before proceeding.'
                  : 'Please fill all required fields in the current step correctly.',
      });
    }
  }
  prevStep() {
    if (this.activeStep > 0) {
      this.activeStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.refreshMapSections();
    }
  }

  goToNextStep() {
    this.nextStep();
  }

  goToPreviousStep() {
    this.prevStep();
  }

  minMaxQtyValidator(group: FormGroup): { [key: string]: boolean } | null {
    const min = group.get('minLoad')?.value;
    const max = group.get('maxLoad')?.value;
    if (min !== null && max !== null && min !== '' && max !== '') {
      if (Number(min) > Number(max)) {
        return { minGreaterThanMax: true };
      }
    }
    return null;
  }

  // Multi-check checkbox toggle helpers
  toggleAshCategory(categoryId: any) {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    const idx = formArray.controls.findIndex(
      (ctrl) => String(ctrl.value.categoryId) === String(categoryId),
    );
    if (idx > -1) {
      formArray.removeAt(idx);
    } else {
      formArray.push(
        this.fb.group(
          {
            categoryId: [categoryId],
            minLoad: [
              '',
              [Validators.required, Validators.pattern('^[0-9]{1,5}$'), Validators.min(1)],
            ],
            maxLoad: [
              '',
              [Validators.required, Validators.pattern('^[0-9]{1,5}$'), Validators.min(1)],
            ],
          },
          {
            validators: this.minMaxQtyValidator.bind(this),
          },
        ),
      );
    }
  }

  getCategoryFormGroup(categoryId: any): FormGroup | null {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    const ctrl = formArray.controls.find((c) => String(c.value.categoryId) === String(categoryId));
    return ctrl ? (ctrl as FormGroup) : null;
  }

  isAshCategoryChecked(categoryId: any): boolean {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    if (!formArray) return false;
    return formArray.controls.some((ctrl) => String(ctrl.value.categoryId) === String(categoryId));
  }

  // Geolocation auto-detect GPS
  detectLocation(type: 'plant' | 'company' = 'plant') {
    if (!navigator.geolocation) {
      Toast.fire({ icon: 'warning', title: 'Geolocation not supported.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = pos.coords.latitude.toFixed(6);
        const longitude = pos.coords.longitude.toFixed(6);
        if (type === 'plant') {
          this.registerForm.patchValue({ latitude, longitude });
          this.registerForm.get('latitude')?.updateValueAndValidity();
          this.registerForm.get('longitude')?.updateValueAndValidity();
          this.updateMapMarkerFromCoordinates('plant', latitude, longitude);
        } else {
          this.registerForm.patchValue({ companyLatitude: latitude, companyLongitude: longitude });
          this.registerForm.get('companyLatitude')?.updateValueAndValidity();
          this.registerForm.get('companyLongitude')?.updateValueAndValidity();
          this.updateMapMarkerFromCoordinates('company', latitude, longitude);
        }
      },
      () =>
        Toast.fire({
          icon: 'warning',
          title: 'Unable to detect location. Please select on the map.',
        }),
    );
  }

  private scheduleLocationMapsInit(): void {
    if (this.activeStep !== this.addressStepIndex) {
      this.destroyInteractiveLocationMaps();
      return;
    }

    if (this.locationMapInitTimer) {
      clearTimeout(this.locationMapInitTimer);
    }

    this.locationMapInitTimer = setTimeout(() => {
      this.initLocationMaps();
    }, 0);
  }

  private schedulePreviewMapsInit(): void {
    if (this.activeStep !== this.previewStepIndex) {
      this.destroyPreviewLocationMaps();
      return;
    }

    if (this.previewMapInitTimer) {
      clearTimeout(this.previewMapInitTimer);
    }

    this.previewMapInitTimer = setTimeout(() => {
      this.initPreviewMaps();
    }, 0);
  }

  private refreshMapSections(): void {
    this.scheduleLocationMapsInit();
    this.schedulePreviewMapsInit();
  }

  private destroyInteractiveLocationMaps(): void {
    this.plantLocationMap?.destroy();
    this.companyLocationMap?.destroy();
    this.plantLocationMap = null;
    this.companyLocationMap = null;
  }

  private destroyPreviewLocationMaps(): void {
    this.previewPlantMap?.destroy();
    this.previewCompanyMap?.destroy();
    this.previewPlantMap = null;
    this.previewCompanyMap = null;
  }

  private initLocationMaps(): void {
    this.googleMapsLocationService
      .load(environment.googleMapsApiKey)
      .then(() => {
        this.locationMapError = '';
        this.destroyInteractiveLocationMaps();
        this.initPlantLocationMap();
        if (!this.registerForm.get('sameAsPlantAddress')?.value) {
          this.initCompanyLocationMap();
        }
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.locationMapError = 'Unable to load Google Maps. Please try again later.';
        this.cdr.detectChanges();
      });
  }

  private initPreviewMaps(): void {
    const showCompanyPreview = !this.registerForm.get('sameAsPlantAddress')?.value;

    this.googleMapsLocationService
      .load(environment.googleMapsApiKey)
      .then(() => {
        this.previewMapError = '';
        this.destroyPreviewLocationMaps();

        const plantContainer = this.previewPlantMapContainer?.nativeElement;
        if (plantContainer) {
          this.previewPlantMap = this.googleMapsLocationService.createPreviewMap(plantContainer, {
            defaultCenter: this.defaultMapCenter,
            defaultZoom: this.defaultMapZoom,
            selectedZoom: this.selectedMapZoom,
            coords: this.getCoordinatesFromForm('plant'),
          });
        }

        if (showCompanyPreview) {
          const companyContainer = this.previewCompanyMapContainer?.nativeElement;
          if (companyContainer) {
            this.previewCompanyMap = this.googleMapsLocationService.createPreviewMap(
              companyContainer,
              {
                defaultCenter: this.defaultMapCenter,
                defaultZoom: this.defaultMapZoom,
                selectedZoom: this.selectedMapZoom,
                coords: this.getCoordinatesFromForm('company'),
              },
            );
          }
        }

        this.cdr.detectChanges();
      })
      .catch(() => {
        this.previewMapError = 'Unable to load Google Maps preview. Please try again later.';
        this.cdr.detectChanges();
      });
  }

  private initPlantLocationMap(): void {
    const container = this.plantMapContainer?.nativeElement;
    if (!container) {
      return;
    }

    this.plantLocationMap = this.googleMapsLocationService.createInteractiveMap(container, {
      defaultCenter: this.defaultMapCenter,
      defaultZoom: this.defaultMapZoom,
      selectedZoom: this.selectedMapZoom,
      initialCoords: this.getCoordinatesFromForm('plant'),
      onLocationChange: (lat, lng) => this.patchLocationCoordinates('plant', lat, lng),
    });

    const searchInput = this.plantLocationSearch?.nativeElement;
    if (searchInput) {
      this.plantLocationMap.bindSearchInput(searchInput);
    }
  }

  private initCompanyLocationMap(): void {
    const container = this.companyMapContainer?.nativeElement;
    if (!container) {
      return;
    }

    this.companyLocationMap = this.googleMapsLocationService.createInteractiveMap(container, {
      defaultCenter: this.defaultMapCenter,
      defaultZoom: this.defaultMapZoom,
      selectedZoom: this.selectedMapZoom,
      initialCoords: this.getCoordinatesFromForm('company'),
      onLocationChange: (lat, lng) => this.patchLocationCoordinates('company', lat, lng),
    });

    const searchInput = this.companyLocationSearch?.nativeElement;
    if (searchInput) {
      this.companyLocationMap.bindSearchInput(searchInput);
    }
  }

  private getCoordinatesFromForm(type: 'plant' | 'company'): { lat: number; lng: number } | null {
    const latField = type === 'plant' ? 'latitude' : 'companyLatitude';
    const lngField = type === 'plant' ? 'longitude' : 'companyLongitude';
    return this.googleMapsLocationService.parseCoordinates(
      this.registerForm.get(latField)?.value,
      this.registerForm.get(lngField)?.value,
    );
  }

  private patchLocationCoordinates(type: 'plant' | 'company', lat: number, lng: number): void {
    const latitude = lat.toFixed(6);
    const longitude = lng.toFixed(6);

    if (type === 'plant') {
      this.registerForm.patchValue({ latitude, longitude });
      this.registerForm.get('latitude')?.updateValueAndValidity();
      this.registerForm.get('longitude')?.updateValueAndValidity();
    } else {
      this.registerForm.patchValue({ companyLatitude: latitude, companyLongitude: longitude });
      this.registerForm.get('companyLatitude')?.updateValueAndValidity();
      this.registerForm.get('companyLongitude')?.updateValueAndValidity();
    }
  }

  private updateMapMarkerFromCoordinates(
    type: 'plant' | 'company',
    latitude: string,
    longitude: string,
  ): void {
    const coords = this.googleMapsLocationService.parseCoordinates(latitude, longitude);
    if (!coords) {
      return;
    }

    const handle = type === 'plant' ? this.plantLocationMap : this.companyLocationMap;
    if (!handle) {
      this.refreshMapSections();
      return;
    }

    handle.setMarker(coords.lat, coords.lng, true);
  }

  // Handle popup-based uploads
  openUploadPopup(fileKey: string) {
    this.activeUploadDocKey = fileKey;
    this.selectedFileForUpload = null;
    this.isUploading = false;
    this.uploadProgress = 0;
  }

  closeUploadPopup() {
    this.activeUploadDocKey = null;
    this.selectedFileForUpload = null;
    this.isUploading = false;
    this.uploadProgress = 0;
  }

  onModalFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      if (this.activeUploadDocKey === 'gpsMapCameraPhoto') {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          Toast.fire({
            icon: 'error',
            title: 'Only image files (JPEG, PNG) are allowed for Plant Photo.',
          });
          event.target.value = '';
          return;
        }
      } else {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          Toast.fire({ icon: 'error', title: 'Only PDF and image files are allowed.' });
          event.target.value = '';
          return;
        }
      }

      if (file.size > 2 * 1024 * 1024) {
        Toast.fire({ icon: 'error', title: 'File size exceeds the 2MB limit.' });
        event.target.value = '';
        return;
      }
      this.selectedFileForUpload = file;
    }
  }

  confirmUpload() {
    if (!this.selectedFileForUpload || !this.activeUploadDocKey) {
      return;
    }

    const docKey = this.activeUploadDocKey;
    const docTypeId = this.documentTypeIds[docKey];

    if (!docTypeId) {
      Toast.fire({ icon: 'error', title: 'Invalid document type selected.' });
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 15;
    this.cdr.detectChanges();

    this.registerApiService
      .uploadDocument(docTypeId, this.selectedFileForUpload)
      .pipe(
        finalize(() => {
          this.isUploading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.uploadProgress = 100;
          this.cdr.detectChanges();

          // Save file reference and document ID returned from backend API
          this.uploadedFiles[docKey] = this.selectedFileForUpload;

          // Use document ID from API response, or fallback gracefully
          const docId =
            res?.data?.id ||
            res?.data?.documentId ||
            res?.id ||
            `DOC-${docTypeId}-${Math.floor(1000 + Math.random() * 9000)}`;
          this.uploadedFileIds[docKey] = docId;

          setTimeout(() => {
            this.closeUploadPopup();
            this.cdr.detectChanges();
            Toast.fire({
              icon: 'success',
              title: `${this.docLabels[docKey]} uploaded successfully!`,
            });
          }, 150);
        },
        error: (err) => {
          console.error('File upload failed:', err);
          Toast.fire({
            icon: 'error',
            title: `Failed to upload ${this.docLabels[docKey]}. Please try again.`,
          });
        },
      });
  }

  onSaveDraft(silent: boolean = false) {
    const mobileNo = this.getRegistrationMobileNo();
    if (!this.canSaveDraft()) {
      if (!silent) {
        Toast.fire({
          icon: 'warning',
          title: 'Enter a valid 10-digit mobile number before saving a draft.',
        });
      }
      return;
    }

    const apiStep = this.activeStep + 1;
    const payload = {
      mobileNo,
      registrationType: this.manufacturingRegistrationType,
      step: apiStep,
      formData: this.buildDraftStepFormData(apiStep),
    };

    if (!silent) {
      this.isSavingDraft = true;
    }

    this.registerApiService
      .saveRegistrationDraftStep(payload)
      .pipe(
        finalize(() => {
          if (!silent) {
            this.isSavingDraft = false;
          }
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          if (!silent) {
            Toast.fire({
              icon: 'success',
              title: res?.message || `Draft saved for step ${apiStep}.`,
            });
          }
        },
        error: (err) => {
          if (!silent) {
            let errorMsg = 'Failed to save draft. Please try again.';
            if (err?.error?.message) {
              if (typeof err.error.message === 'object') {
                errorMsg = Object.values(err.error.message).join(', ');
              } else {
                errorMsg = err.error.message;
              }
            }
            Toast.fire({
              icon: 'error',
              title: errorMsg,
            });
          }
        },
      });
  }

  onResumeDraft() {
    Swal.fire({
      title: 'Resume Draft Registration',
      text: 'Enter the mobile number used while saving your draft.',
      input: 'tel',
      inputLabel: 'Registered Mobile Number',
      inputPlaceholder: 'Enter 10-digit mobile number',
      inputAttributes: {
        maxlength: '10',
        inputmode: 'numeric',
      },
      showCancelButton: true,
      confirmButtonColor: '#00796b',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Load Draft',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'premium-swal-popup',
        title: 'premium-swal-title',
        confirmButton: 'premium-swal-btn',
        cancelButton: 'premium-swal-btn',
      },
      preConfirm: (value) => {
        const mobileNo = String(value || '').trim();
        if (!/^[6-9][0-9]{9}$/.test(mobileNo)) {
          Swal.showValidationMessage('Enter a valid 10-digit mobile number.');
          return false;
        }
        return mobileNo;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        this.fetchAndApplyDraftDetail(String(result.value));
      }
    });
  }

  canSaveDraft(): boolean {
    const mobileNo = this.getRegistrationMobileNo();
    return /^[6-9][0-9]{9}$/.test(mobileNo);
  }

  private getRegistrationMobileNo(): string {
    return String(this.registerForm.get('mobileNumber')?.value || '').trim();
  }

  private buildDraftStepFormData(apiStep: number): Record<string, unknown> {
    const form = this.registerForm.getRawValue();
    const capacity = Number(form.capacity) || 0;

    switch (apiStep) {
      case 1:
        return {
          powerStationsId: form.station ? Number(form.station) : null,
          mobileNo: form.mobileNumber,
        };
      case 2:
        return {
          firmName: form.firmName || '',
          email: form.emailAddress || '',
          tariff: form.tariff ? Number(form.tariff) : null,
          panNo: form.panNumber || '',
          gstNo: form.gstNumber || '',
          msmeUdayamNo: form.msmeRegistrationNumber || '',
          msmeUdayamCertificate: form.msmeCertificateType || '',
          tnebScNo: form.tnpgclScNumber || '',
          // ctoDetails: form.consentToOperate || '',
        };
      case 3: {
        const isSameAddress = form.sameAsPlantAddress === true;
        return {
          doorNo: form.doorNo || '',
          sfNo: form.surveyFieldNo || '',
          street: form.streetLocation || '',
          stateId: form.state ? Number(form.state) : null,
          districtId: form.district ? Number(form.district) : null,
          talukId: form.taluk ? Number(form.taluk) : null,
          villageId: form.village ? Number(form.village) : null,
          pincode: form.pincode || '',
          locationLat: form.latitude ? String(form.latitude) : '',
          locationLng: form.longitude ? String(form.longitude) : '',
          isCompanyAddressSameAsPlant: isSameAddress,
          companyStateId: isSameAddress
            ? form.state
              ? Number(form.state)
              : null
            : form.companyState
              ? Number(form.companyState)
              : null,
          companyDistrictId: isSameAddress
            ? form.district
              ? Number(form.district)
              : null
            : form.companyDistrict
              ? Number(form.companyDistrict)
              : null,
          companyTalukId: isSameAddress
            ? form.taluk
              ? Number(form.taluk)
              : null
            : form.companyTaluk
              ? Number(form.companyTaluk)
              : null,
          companyVillageId: isSameAddress
            ? form.village
              ? Number(form.village)
              : null
            : form.companyVillage
              ? Number(form.companyVillage)
              : null,
          companyDoorNo: isSameAddress ? form.doorNo || '' : form.companyDoorNo || '',
          companySfNo: isSameAddress ? form.surveyFieldNo || '' : form.companySurveyFieldNo || '',
          companyStreet: isSameAddress
            ? form.streetLocation || ''
            : form.companyStreetLocation || '',
          companyPincode: isSameAddress ? form.pincode || '' : form.companyPincode || '',
          companyLocationLat: isSameAddress
            ? form.latitude
              ? String(form.latitude)
              : ''
            : form.companyLatitude
              ? String(form.companyLatitude)
              : '',
          companyLocationLng: isSameAddress
            ? form.longitude
              ? String(form.longitude)
              : ''
            : form.companyLongitude
              ? String(form.companyLongitude)
              : '',
        };
      }
      case 4:
        return {
          plantMachineriesAvailability: form.plantMachinery || '',
          requiredQuantityOfAsh: (form.ashCategoryDetails || [])
            .reduce((sum: number, detail: any) => sum + Number(detail.minLoad || 0) * capacity, 0)
            .toFixed(2),
          capacity,
          ashCategoryDetails: (form.ashCategoryDetails || []).map((detail: any) => ({
            categoryId: Number(detail.categoryId),
            minLoad: Number(detail.minLoad),
            maxLoad: Number(detail.maxLoad),
            minQuantity: Number(detail.minLoad || 0) * capacity,
            maxQuantity: Number(detail.maxLoad || 0) * capacity,
            quantity: Number(detail.minLoad || 0) * capacity,
          })),
          ashProducts: (form.productType || []).map((id: any) => {
            const productObj = this.flyAshProducts.find(
              (p) => p.id === id || String(p.id) === String(id),
            );
            const isOthers = productObj && (productObj.name || '').toLowerCase().includes('other');

            return {
              productId: Number(id),
              description: isOthers ? form.productDescription || null : null,
            };
          }),
          ashUnloadingFacility: (form.ashUnloadingFacilities || [])
            .filter((facility: any) => facility != null && facility !== '')
            .map((facility: any) => ({
              id: Number(facility),
            })),
        };
      case 5:
        return { documents: this.buildDraftDocsArray() };
      default:
        return {};
    }
  }

  private buildDraftDocsArray(): Array<{ documentTypeId: number; documentUploadId: number }> {
    const docsArray: Array<{ documentTypeId: number; documentUploadId: number }> = [];
    Object.keys(this.uploadedFileIds).forEach((key) => {
      const fileUploadId = this.uploadedFileIds[key];
      const docTypeId = this.documentTypeIds[key];
      if (fileUploadId && docTypeId) {
        docsArray.push({
          documentTypeId: Number(docTypeId),
          documentUploadId: Number(fileUploadId),
        });
      }
    });
    return docsArray;
  }

  private getDraftApiMessage(res: any, fallback: string): string {
    const message = res?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    return fallback;
  }

  private fetchAndApplyDraftDetail(mobileNo: string) {
    this.isLoadingDraft = true;
    this.registerApiService
      .getRegistrationDraftDetail(mobileNo)
      .pipe(
        finalize(() => {
          this.isLoadingDraft = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          const parsed = this.parseDraftDetailResponse(res);
          if (!parsed.found) {
            Toast.fire({
              icon: 'info',
              title: this.getDraftApiMessage(res, 'No saved draft found for this mobile number.'),
            });
            return;
          }

          this.applyRegistrationDraftDetail(parsed.data, parsed.currentStep);
          Toast.fire({
            icon: 'success',
            title: this.getDraftApiMessage(
              res,
              'Draft loaded successfully. Continue your registration.',
            ),
          });
        },
        error: (err) => {
          Toast.fire({
            icon: err?.status === 404 ? 'info' : 'error',
            title:
              err?.error?.message ||
              'Unable to load draft. Please verify the mobile number and try again.',
          });
        },
      });
  }

  private readonly draftPatchOptions = { emitEvent: false };

  private parseDraftDetailResponse(res: any): {
    found: boolean;
    data: any;
    currentStep: number;
  } {
    if (!res) {
      return { found: false, data: {}, currentStep: 1 };
    }

    const root = this.extractDraftResponseRoot(res);
    if (!root) {
      return { found: false, data: {}, currentStep: 1 };
    }

    const mergedData = this.mergeDraftResponseData(root, res);
    const currentStep = this.extractDraftCurrentStep(res, root, mergedData);

    if (!this.hasDraftContent(mergedData)) {
      return { found: false, data: {}, currentStep: 1 };
    }

    return {
      found: true,
      data: mergedData,
      currentStep,
    };
  }

  private extractDraftResponseRoot(res: any): any {
    if (!res || typeof res !== 'object') {
      return null;
    }

    const messagePayload =
      res.message && typeof res.message === 'object' && !Array.isArray(res.message)
        ? res.message
        : null;

    if (messagePayload) {
      const draftFields = this.normalizeDraftFormData(
        messagePayload.data ?? messagePayload.formData ?? messagePayload.draft,
      );

      if (Object.keys(draftFields).length > 0) {
        return {
          ...draftFields,
          currentStep: messagePayload.currentStep,
          draftId: messagePayload.draftId,
          mobileNo: draftFields.mobileNo ?? messagePayload.mobileNo,
          powerStationsId:
            draftFields.powerStationsId ??
            messagePayload.powerStationsId ??
            draftFields.stationId ??
            messagePayload.stationId,
        };
      }

      if (messagePayload.currentStep != null || messagePayload.draftId != null) {
        return messagePayload;
      }
    }

    const dataCandidate = res.data ?? res.result;

    if (Array.isArray(dataCandidate)) {
      if (dataCandidate.length === 0) {
        return null;
      }

      return {
        steps: dataCandidate,
        currentStep: res.currentStep ?? messagePayload?.currentStep,
        mobileNo: res.mobileNo ?? messagePayload?.mobileNo,
      };
    }

    if (dataCandidate && typeof dataCandidate === 'object') {
      if (dataCandidate.detail && typeof dataCandidate.detail === 'object') {
        return {
          ...dataCandidate.detail,
          currentStep: dataCandidate.detail.currentStep ?? dataCandidate.currentStep,
          mobileNo: dataCandidate.detail.mobileNo ?? dataCandidate.mobileNo,
        };
      }

      return dataCandidate;
    }

    return messagePayload ?? res;
  }

  private mergeDraftResponseData(root: any, res: any): any {
    let merged: any = {};

    const stepCollections = [
      root.steps,
      root.stepData,
      root.draftData,
      root.draftSteps,
      root.registrationDraftSteps,
      root.savedSteps,
      root.stepDetails,
      Array.isArray(root.formData) ? root.formData : null,
      Array.isArray(root.draft) ? root.draft : null,
    ].filter(Array.isArray) as any[][];

    stepCollections.forEach((steps) => {
      merged = { ...merged, ...this.mergeDraftStepRecords(steps) };
    });

    Object.keys(root).forEach((key) => {
      const stepMatch = /^step[_-]?(\d+)$/i.exec(key);
      if (stepMatch) {
        merged = { ...merged, ...this.normalizeDraftFormData(root[key]) };
      }
    });

    if (root.formData && !Array.isArray(root.formData)) {
      merged = { ...merged, ...this.normalizeDraftFormData(root.formData) };
    }
    if (root.draft && !Array.isArray(root.draft)) {
      merged = { ...merged, ...this.normalizeDraftFormData(root.draft) };
    }
    if (root.draftData && !Array.isArray(root.draftData)) {
      merged = { ...merged, ...this.normalizeDraftFormData(root.draftData) };
    }

    const metaKeys = new Set([
      'steps',
      'stepData',
      'draftData',
      'draftSteps',
      'registrationDraftSteps',
      'savedSteps',
      'stepDetails',
      'formData',
      'draft',
      'detail',
      'currentStep',
      'current_step',
      'step',
      'lastStep',
      'registrationType',
      'status',
      'message',
      'result',
      'draftId',
    ]);

    Object.entries(root).forEach(([key, value]) => {
      if (metaKeys.has(key) || /^step[_-]?\d+$/i.test(key)) {
        return;
      }
      if (value === null || value === undefined) {
        return;
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        return;
      }
      merged[key] = value;
    });

    merged.mobileNo = merged.mobileNo ?? root.mobileNo ?? res.mobileNo;
    merged.powerStationsId =
      merged.powerStationsId ?? root.powerStationsId ?? merged.stationId ?? root.stationId;

    return merged;
  }

  private mergeDraftStepRecords(steps: any[]): any {
    return steps.reduce((acc: any, item: any) => {
      if (!item || typeof item !== 'object') {
        return acc;
      }

      const payload = item.formData ?? item.data ?? item.stepFormData ?? item.draftData ?? item;
      return { ...acc, ...this.normalizeDraftFormData(payload) };
    }, {});
  }

  private normalizeDraftFormData(value: any): any {
    if (value == null) {
      return {};
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return {};
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return this.mergeDraftStepRecords(parsed);
        }
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch {
        return {};
      }

      return {};
    }

    if (Array.isArray(value)) {
      return this.mergeDraftStepRecords(value);
    }

    if (typeof value === 'object') {
      return value;
    }

    return {};
  }

  private extractDraftCurrentStep(res: any, root: any, mergedData: any): number {
    const messagePayload =
      res.message && typeof res.message === 'object' && !Array.isArray(res.message)
        ? res.message
        : null;

    const stepCollections = [
      root?.steps,
      root?.stepData,
      root?.draftData,
      root?.draftSteps,
      root?.registrationDraftSteps,
      root?.savedSteps,
      root?.stepDetails,
      Array.isArray(root?.formData) ? root.formData : null,
    ].filter(Array.isArray) as any[][];

    const maxSavedStep = stepCollections.reduce((max, steps) => {
      const stepMax = steps.reduce(
        (stepAcc: number, item: any) =>
          Math.max(stepAcc, Number(item?.step ?? item?.stepNo ?? item?.stepNumber) || 0),
        0,
      );
      return Math.max(max, stepMax);
    }, 0);

    const rawStep =
      root?.currentStep ??
      root?.current_step ??
      root?.step ??
      root?.lastStep ??
      mergedData?.currentStep ??
      messagePayload?.currentStep ??
      res.currentStep ??
      (maxSavedStep > 0 ? maxSavedStep : 1);

    const parsed = Number(rawStep);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  private hasDraftContent(data: any): boolean {
    return Boolean(
      data.mobileNo ||
      data.powerStationsId ||
      data.stationId ||
      data.firmName ||
      data.email ||
      data.doorNo ||
      data.plantMachineriesAvailability ||
      (Array.isArray(data.ashProducts) && data.ashProducts.length) ||
      (Array.isArray(data.ashCategoryDetails) && data.ashCategoryDetails.length) ||
      (Array.isArray(data.documents) && data.documents.length),
    );
  }

  private coerceDraftBoolean(value: any, defaultValue = true): boolean {
    if (value === true || value === 1 || value === '1' || value === 'true') {
      return true;
    }
    if (value === false || value === 0 || value === '0' || value === 'false') {
      return false;
    }
    return defaultValue;
  }

  private coerceDraftSelectValue(value: any): string | number {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : value;
  }

  private applyRegistrationDraftDetail(data: Record<string, any>, currentStep: number) {
    Object.keys(this.uploadedFiles).forEach((key) => {
      this.uploadedFiles[key] = null;
      this.uploadedFileIds[key] = null;
    });

    this.applyDraftStep1(data);
    this.applyDraftStep2(data);
    this.applyDraftStep3(data);
    this.restoreAshProducts(data);
    const pmVal = data['plantMachineriesAvailability'] ?? '';
    this.plantMachineriesTags = pmVal
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => s);

    this.registerForm.patchValue(
      {
        plantMachinery: pmVal,
        capacity: data['capacity'] ?? '',
      },
      this.draftPatchOptions,
    );
    this.restoreAshCategoriesAndUnloading(data);
    this.applyDraftStep5(data);

    const mobileNo = String(data['mobileNo'] ?? '').trim();
    if (/^[6-9][0-9]{9}$/.test(mobileNo)) {
      this.mobileCheckStatus = 'valid';
      this.mobileOtpVerified = true;
      this.otpVerifiedMobile = mobileNo;
      this.registerForm.get('mobileNumber')?.disable({ emitEvent: false });
    }

    const email = String(data['email'] ?? '').trim();
    if (email) {
      this.emailOtpVerified = true;
      this.otpVerifiedEmail = email;
      this.registerForm.get('emailAddress')?.disable({ emitEvent: false });
    }

    const apiStep = Number.isFinite(currentStep) && currentStep > 0 ? currentStep : 1;
    this.activeStep = Math.min(Math.max(apiStep - 1, 0), 4);
    this.draftLoadedForMobile = String(mobileNo || this.getRegistrationMobileNo());
    this.cdr.detectChanges();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.refreshMapSections();
  }

  private restoreAshProducts(data: any) {
    this.selectedProductIds = (data.ashProducts || []).map((product: any) =>
      Number(product.productId),
    );
    const othersProduct = (data.ashProducts || []).find((product: any) => product.description);
    this.registerForm.patchValue(
      {
        productType: this.selectedProductIds.length ? this.selectedProductIds : null,
        productDescription: othersProduct?.description ?? '',
      },
      this.draftPatchOptions,
    );
    this.updateProductDescriptionValidation();
  }

  private restoreAshCategoriesAndUnloading(data: any) {
    const stationId =
      data.powerStationsId ?? data.stationId ?? this.registerForm.get('station')?.value;
    const msme = data.msmeUdayamCertificate ?? this.registerForm.get('msmeCertificateType')?.value;
    const productIds = this.selectedProductIds;

    const restoreCategories = () => {
      const categoryArray = this.registerForm.get('ashCategoryDetails') as FormArray;
      categoryArray.clear();
      (data.ashCategoryDetails || []).forEach((detail: any) => {
        categoryArray.push(
          this.fb.group(
            {
              categoryId: [Number(detail.categoryId)],
              minLoad: [detail.minLoad ?? '', [Validators.required]],
              maxLoad: [detail.maxLoad ?? '', [Validators.required]],
            },
            { validators: this.minMaxQtyValidator.bind(this) },
          ),
        );
      });

      const unloadArray = this.registerForm.get('ashUnloadingFacilities') as FormArray;
      unloadArray.clear();
      (data.ashUnloadingFacility || []).forEach((facility: any) => {
        unloadArray.push(this.fb.control(Number(facility.id)));
      });
      this.cdr.detectChanges();
    };

    if (stationId && msme && productIds.length) {
      this.registerApiService.getAshCategories(stationId, msme, productIds).subscribe({
        next: (res) => {
          this.ashCategoriesList = this.extractData(res);
          restoreCategories();
        },
        error: () => restoreCategories(),
      });
      return;
    }

    restoreCategories();
  }

  private applyDraftStep1(data: any) {
    this.registerForm.patchValue(
      {
        station: this.coerceDraftSelectValue(data.powerStationsId ?? data.stationId),
        mobileNumber: data.mobileNo ?? this.getRegistrationMobileNo(),
      },
      this.draftPatchOptions,
    );
  }

  private applyDraftStep2(data: any) {
    const msmeCertificateType = data.msmeUdayamCertificate ?? '';

    this.registerForm.patchValue(
      {
        firmName: data.firmName ?? '',
        emailAddress: data.email ?? '',
        tariff: this.coerceDraftSelectValue(data.tariff),
        panNumber: data.panNo ?? '',
        gstNumber: data.gstNo ?? '',
        msmeRegistrationNumber: data.msmeUdayamNo ?? '',
        msmeCertificateType,
        tnpgclScNumber: data.tnebScNo ?? '',
      },
      this.draftPatchOptions,
    );

    const consentControl = this.registerForm.get('consentToOperate');
    if (msmeCertificateType) {
      consentControl?.setValidators([Validators.required]);
    } else {
      consentControl?.clearValidators();
    }
    consentControl?.updateValueAndValidity(this.draftPatchOptions);
    // consentControl?.setValue(data.ctoDetails ?? '', this.draftPatchOptions);
  }

  private applyDraftStep3(data: any) {
    const isSameAddress = this.coerceDraftBoolean(data.isCompanyAddressSameAsPlant, true);

    this.registerForm.patchValue(
      {
        doorNo: data.doorNo ?? '',
        surveyFieldNo: data.sfNo ?? '',
        streetLocation: data.street ?? '',
        pincode: data.pincode ?? '',
        latitude: data.locationLat ?? '',
        longitude: data.locationLng ?? '',
        sameAsPlantAddress: isSameAddress,
      },
      this.draftPatchOptions,
    );

    if (isSameAddress) {
      this.registerForm.patchValue(
        {
          companyDoorNo: data.doorNo ?? '',
          companySurveyFieldNo: data.sfNo ?? '',
          companyStreetLocation: data.street ?? '',
          companyPincode: data.pincode ?? '',
          companyLatitude: data.locationLat ?? '',
          companyLongitude: data.locationLng ?? '',
        },
        this.draftPatchOptions,
      );
    } else {
      this.registerForm.patchValue(
        {
          companyDoorNo: data.companyDoorNo ?? '',
          companySurveyFieldNo: data.companySfNo ?? '',
          companyStreetLocation: data.companyStreet ?? '',
          companyPincode: data.companyPincode ?? '',
          companyLatitude: data.companyLocationLat ?? '',
          companyLongitude: data.companyLocationLng ?? '',
        },
        this.draftPatchOptions,
      );
    }

    this.restoreLocationDropdowns(
      data.stateId,
      data.districtId,
      data.talukId,
      data.villageId,
      'plant',
    );

    if (isSameAddress) {
      this.restoreLocationDropdowns(
        data.stateId ?? data.companyStateId,
        data.districtId ?? data.companyDistrictId,
        data.talukId ?? data.companyTalukId,
        data.villageId ?? data.companyVillageId,
        'company',
      );
    } else {
      this.restoreLocationDropdowns(
        data.companyStateId,
        data.companyDistrictId,
        data.companyTalukId,
        data.companyVillageId,
        'company',
      );
    }
  }

  private restoreLocationDropdowns(
    stateId: number | null | undefined,
    districtId: number | null | undefined,
    talukId: number | null | undefined,
    villageId: number | null | undefined,
    type: 'plant' | 'company',
  ) {
    if (!stateId) {
      return;
    }

    const stateControl = type === 'company' ? 'companyState' : 'state';
    const districtControl = type === 'company' ? 'companyDistrict' : 'district';
    const talukControl = type === 'company' ? 'companyTaluk' : 'taluk';
    const villageControl = type === 'company' ? 'companyVillage' : 'village';
    const loadDistrictsFn = this.registerApiService.getDistricts.bind(this.registerApiService);
    const loadTaluksFn = this.registerApiService.getTaluks.bind(this.registerApiService);
    const loadVillagesFn = this.registerApiService.getVillages.bind(this.registerApiService);

    this.registerForm.get(stateControl)?.setValue(stateId, { emitEvent: false });

    loadDistrictsFn(stateId).subscribe({
      next: (res) => {
        const list = this.extractData(res);
        if (type === 'company') {
          this.companyDistricts = list;
        } else {
          this.districts = list;
        }

        if (districtId) {
          this.registerForm.get(districtControl)?.setValue(districtId, { emitEvent: false });
          loadTaluksFn(districtId).subscribe({
            next: (talukRes) => {
              const talukList = this.extractData(talukRes);
              if (type === 'company') {
                this.companyTaluks = talukList;
              } else {
                this.taluks = talukList;
              }

              if (talukId) {
                this.registerForm.get(talukControl)?.setValue(talukId, { emitEvent: false });
                loadVillagesFn(talukId).subscribe({
                  next: (villageRes) => {
                    const villageList = this.extractData(villageRes);
                    if (type === 'company') {
                      this.companyVillages = villageList;
                    } else {
                      this.villages = villageList;
                    }

                    if (villageId) {
                      this.registerForm
                        .get(villageControl)
                        ?.setValue(villageId, { emitEvent: false });
                    }
                    this.cdr.detectChanges();
                  },
                });
              }
            },
          });
        }
        this.cdr.detectChanges();
      },
    });
  }

  private applyDraftStep5(data: any) {
    (data.documents || []).forEach((doc: any) => {
      const docKey = this.getDocumentKeyByTypeId(Number(doc.documentTypeId));
      if (!docKey || !doc.documentUploadId) {
        return;
      }

      this.uploadedFileIds[docKey] = String(doc.documentUploadId);
      this.uploadedFiles[docKey] = new File(
        ['draft'],
        `${this.docLabels[docKey] || docKey}-draft.pdf`,
        { type: 'application/pdf' },
      );
    });
  }

  private getDocumentKeyByTypeId(typeId: number): string | null {
    const match = Object.entries(this.documentTypeIds).find(
      ([, id]) => Number(id) === Number(typeId),
    );
    return match ? match[0] : null;
  }

  onPreview() {
    // if (this.registerForm.invalid) {
    //   this.registerForm.markAllAsTouched();
    //   Toast.fire({ icon: 'warning', title: 'Please fill all mandatory fields to preview.' });
    //   return;
    // }
    this.showFormPreview = true;
  }

  submitFromPreview() {
    Swal.fire({
      title: 'Confirm Submission',
      text: 'Are you sure you want to submit your application?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00796b',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, submit it!',
      cancelButtonText: 'Review again',
      customClass: {
        popup: 'premium-swal-popup',
        title: 'premium-swal-title',
        confirmButton: 'premium-swal-btn',
        cancelButton: 'premium-swal-btn',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.showFormPreview = false;
        this.handleSubmit();
      }
    });
  }

  getStationName(): string {
    const id = this.registerForm.get('station')?.value;
    const item = this.powerStations.find((x) => x.id == id || x.name === id);
    return item ? item.name : id || 'N/A';
  }

  getTariffName(): string {
    const id = this.registerForm.get('tariff')?.value;
    const item = this.tariff.find((x) => x.id == id || x.name === id);
    return item ? item.name : id || 'N/A';
  }

  getProductName(): string {
    const products = this.getSelectedProducts();
    if (products.length === 0) {
      return 'N/A';
    }
    return products.map((p) => p.name).join(', ');
  }

  getStateName(type: 'plant' | 'company' = 'plant'): string {
    const id = this.registerForm.get(type === 'plant' ? 'state' : 'companyState')?.value;
    const item = this.states.find((x) => x.id == id || x.name === id);
    return item ? item.name : id || 'N/A';
  }

  getDistrictName(type: 'plant' | 'company' = 'plant'): string {
    const id = this.registerForm.get(type === 'plant' ? 'district' : 'companyDistrict')?.value;
    const list = type === 'plant' ? this.districts : this.companyDistricts;
    const item = list.find((x: any) => x.id == id || x.name === id || x === id);
    return item ? (item.name ?? item) : id || 'N/A';
  }

  getTalukName(type: 'plant' | 'company' = 'plant'): string {
    const id = this.registerForm.get(type === 'plant' ? 'taluk' : 'companyTaluk')?.value;
    const list = type === 'plant' ? this.taluks : this.companyTaluks;
    const item = list.find((x: any) => x.id == id || x.name === id || x === id);
    return item ? (item.name ?? item) : id || 'N/A';
  }

  getVillageName(type: 'plant' | 'company' = 'plant'): string {
    const id = this.registerForm.get(type === 'plant' ? 'village' : 'companyVillage')?.value;
    const list = type === 'plant' ? this.villages : this.companyVillages;
    const item = list.find((x: any) => x.id == id || x.name === id || x === id);
    return item ? (item.name ?? item) : id || 'N/A';
  }

  handleSubmit() {
    const declarationChecked = this.registerForm.get('declarationCheck')?.value;
    const declarationChecked2 = this.registerForm.get('declarationCheck2')?.value;
    if (!declarationChecked || !declarationChecked2) {
      Toast.fire({
        icon: 'warning',
        title: 'Please read and accept all declarations before submitting.',
      });
      return;
    }

    // GST/PAN fields must be in uppercase
    const gstVal = this.registerForm.get('gstNumber')?.value;
    if (gstVal) {
      this.registerForm.get('gstNumber')?.setValue(gstVal.toUpperCase());
    }
    const panVal = this.registerForm.get('panNumber')?.value;
    if (panVal) {
      this.registerForm.get('panNumber')?.setValue(panVal.toUpperCase());
    }

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      Toast.fire({
        icon: 'warning',
        title: 'Please fill all mandatory fields and resolve errors.',
      });
      return;
    }

    // Required files validation check
    const requiredFiles = [
      'gstCertificate',
      'panCard',
      'previousEbBill',
      'gstr3b',
      'landOwnership',
      'propertyTax',
    ];
    const missingFiles = requiredFiles.filter((key) => !this.uploadedFiles[key]);
    if (missingFiles.length > 0) {
      Toast.fire({ icon: 'warning', title: 'Please upload all required supporting documents.' });
      return;
    }

    Toast.fire({ icon: 'info', title: 'Submitting your registration application...' });
    // Map form fields to API payload
    const form = this.registerForm.getRawValue();

    // Extract uploaded documents with their type ID and uploaded file ID
    const docsArray: any[] = [];
    Object.keys(this.uploadedFileIds).forEach((key) => {
      const fileUploadId = this.uploadedFileIds[key];
      const docTypeId = this.documentTypeIds[key];
      if (fileUploadId && docTypeId) {
        docsArray.push({
          documentTypeId: Number(docTypeId),
          documentUploadId: Number(fileUploadId),
        });
      }
    });

    // Transform ash products from ID array to object array with productId
    const ashProductsArray = (form.productType || []).map((id: any) => {
      const productObj = this.flyAshProducts.find(
        (p) => p.id === id || String(p.id) === String(id),
      );
      const isOthers = productObj && (productObj.name || '').toLowerCase().includes('other');

      return {
        productId: String(id),
        description: isOthers ? form.productDescription : null,
      };
    });

    // Transform uploading facility to ash unloading facility array of objects
    // Filter out null/empty values to avoid database constraint violations
    const ashUnloadingFacilityArray = (form.ashUnloadingFacilities || [])
      .filter((facility: any) => facility != null && facility !== '')
      .map((facility: any) => ({
        id: Number(facility),
      }));

    // Determine company address values
    const isSameAddress = form.sameAsPlantAddress === true;
    const companyStateId = isSameAddress ? Number(form.state) : Number(form.companyState);
    const companyDistrictId = isSameAddress ? Number(form.district) : Number(form.companyDistrict);
    const companyTalukId = isSameAddress ? Number(form.taluk) : Number(form.companyTaluk);
    const companyVillageId = isSameAddress ? Number(form.village) : Number(form.companyVillage);
    const companyDoorNo = isSameAddress ? form.doorNo : form.companyDoorNo;
    const companySfNo = isSameAddress ? form.surveyFieldNo : form.companySurveyFieldNo;
    const companyStreet = isSameAddress ? form.streetLocation : form.companyStreetLocation;
    const companyPincode = isSameAddress ? form.pincode : form.companyPincode;
    const companyLocationLat = isSameAddress
      ? form.latitude
        ? form.latitude.toString()
        : ''
      : form.companyLatitude
        ? form.companyLatitude.toString()
        : '';
    const companyLocationLng = isSameAddress
      ? form.longitude
        ? form.longitude.toString()
        : ''
      : form.companyLongitude
        ? form.companyLongitude.toString()
        : '';

    const capacity = Number(form.capacity) || 0;
    const payload = {
      registrationType: '1',
      registrationTypeName: 'Manufacturing',
      powerStationsId: Number(form.station),
      ashCategories: (form.ashCategoryDetails || []).map((detail: any) => {
        let calcQty = Number(detail.minLoad || 0) * capacity;
        if (calcQty > 0 && calcQty < 100) calcQty = 100;
        return {
          id: Number(detail.categoryId),
          qty: calcQty, // fallback logic from existing qty usage
        };
      }),

      ashCategoryDetails: (form.ashCategoryDetails || []).map((detail: any) => {
        let calcMin = Number(detail.minLoad || 0) * capacity;
        let calcMax = Number(detail.maxLoad || 0) * capacity;
        return {
          categoryId: Number(detail.categoryId),
          minLoad: Number(detail.minLoad),
          maxLoad: Number(detail.maxLoad),
          minQuantity: calcMin,
          maxQuantity: calcMax,
          quantity: calcMin, // fallback for legacy logic
        };
      }),
      ashProducts: ashProductsArray,
      ashUnloadingFacility: ashUnloadingFacilityArray,
      firmName: form.firmName,
      stateId: Number(form.state),
      districtId: Number(form.district),
      talukId: Number(form.taluk),
      villageId: Number(form.village),
      doorNo: form.doorNo,
      sfNo: form.surveyFieldNo,
      street: form.streetLocation,
      pincode: form.pincode,
      mobileNo: form.mobileNumber,
      email: form.emailAddress,
      gstNo: form.gstNumber,
      panNo: form.panNumber,
      msmeUdayamNo: form.msmeRegistrationNumber,
      msmeUdayamCertificate: form.msmeCertificateType, // Micro / Small
      ctoDetails: form.consentToOperate || '',
      tnebScNo: form.tnpgclScNumber,
      capacity: Number(form.capacity) || capacity || 0,
      tariff: Number(form.tariff),
      plantMachineriesAvailability: form.plantMachinery,
      requiredQuantityOfAsh: (form.ashCategoryDetails || []).reduce((sum: number, detail: any) => {
        let calcQty = Number(detail.minLoad || 0) * capacity;
        if (calcQty > 0 && calcQty < 100) calcQty = 100;
        return sum + calcQty;
      }, 0),
      locationLat: form.latitude ? form.latitude.toString() : '',
      locationLng: form.longitude ? form.longitude.toString() : '',
      isCompanyAddressSameAsPlant: isSameAddress,
      // Company Address fields (populated from plant address if same, otherwise from company fields)
      companyStateId: companyStateId,
      companyDistrictId: companyDistrictId,
      companyTalukId: companyTalukId,
      companyVillageId: companyVillageId,
      companyDoorNo: companyDoorNo,
      companySfNo: companySfNo,
      companyStreet: companyStreet,
      companyPincode: companyPincode,
      companyLocationLat: companyLocationLat,
      companyLocationLng: companyLocationLng,
      documents: docsArray,
    };

    this.registerApiService.createRegistration(payload).subscribe({
      next: (res) => {
        const appNo =
          res?.data?.applicationNo ||
          res?.data?.result?.applicationNo ||
          'TNEB-FLY-' + Math.floor(100000 + Math.random() * 900000);
        Swal.fire({
          title: 'Application Submitted',
          html: `Your Application No.: <strong>${appNo}</strong><br><br>An acknowledgement will be sent to your registered email and mobile number.`,
          icon: 'success',
          confirmButtonText: 'Great!',
          confirmButtonColor: '#28a745',
          customClass: {
            popup: 'premium-swal-popup',
            title: 'premium-swal-title',
            confirmButton: 'premium-swal-btn',
          },
        }).then(() => {
          this.router.navigate(['/login']);
        });
      },
      error: (err) => {
        console.error('Registration failed:', err);
        Swal.fire({
          title: 'Submission Failed',
          text:
            err?.error?.message ||
            'Failed to submit registration. Please check the fields and try again.',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#dc3545',
          customClass: {
            popup: 'premium-swal-popup',
            title: 'premium-swal-title',
            confirmButton: 'premium-swal-btn',
          },
        });
      },
    });
  }

  previewDocumentUrl: SafeResourceUrl | null = null;
  previewDownloadUrl: string | null = null;
  previewDownloadName: string | null = null;

  viewUploadedFile(file: File | null, docKey?: string) {
    if (!file) return;

    if (file.name.endsWith('-draft.pdf') || file.size === 5) {
      if (!docKey) return;
      const uploadId = this.uploadedFileIds[docKey];
      if (!uploadId) return;

      this.registerApiService.downloadDocument(uploadId).subscribe({
        next: (blob: Blob) => {
          let type = 'application/pdf';
          const nameLower = file.name.toLowerCase();
          if (nameLower.endsWith('.png') || nameLower.endsWith('-draft.png')) type = 'image/png';
          else if (nameLower.endsWith('.jpg') || nameLower.endsWith('.jpeg') || nameLower.endsWith('-draft.jpg') || nameLower.endsWith('-draft.jpeg')) type = 'image/jpeg';

          const pdfBlob = new Blob([blob], { type });
          const fileURL = URL.createObjectURL(pdfBlob);
          this.previewDownloadUrl = fileURL;
          this.previewDownloadName = file.name.replace('-draft.pdf', '.pdf').replace('-draft.png', '.png').replace('-draft.jpg', '.jpg').replace('-draft.jpeg', '.jpeg');
          this.previewDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to load draft document:', err);
          Toast.fire({ icon: 'error', title: 'Failed to load document preview' });
        },
      });
      return;
    }

    const fileURL = URL.createObjectURL(file);
    this.previewDownloadUrl = fileURL;
    this.previewDownloadName = file.name;
    this.previewDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
  }

  closeDocumentPreview() {
    this.previewDocumentUrl = null;
    this.previewDownloadUrl = null;
    this.previewDownloadName = null;
  }

  downloadPreviewDocument() {
    if (this.previewDownloadUrl && this.previewDownloadName) {
      const a = document.createElement('a');
      a.href = this.previewDownloadUrl;
      a.download = this.previewDownloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  showPreviewModal = false;
  isSavingDraft = false;
  isLoadingDraft = false;
  private draftLoadedForMobile = '';
  private readonly manufacturingRegistrationType = 1;

  openPreviewModal() {
    this.showPreviewModal = true;
  }

  closePreviewModal() {
    this.showPreviewModal = false;
  }

  getMachineryList(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
  }
}
